'use client';

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@/components/i18n-provider';
import { useIsMd } from '@/components/workspace/panels/panel-shell';
import { useHandsetLandscape } from '@/hooks/use-handset-landscape';
import {
  Eye,
  Code,
  Maximize2,
  Share2,
  Settings,
  Lock,
  Link2,
  Check,
  Copy,
  Loader2,
  AlertTriangle,
  Save,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Bot,
  History as HistoryIcon,
  Blocks,
  GitFork,
  GitMerge,
  Info,
  Rocket,
  RefreshCw,
  X,
  PenLine,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import { useReactFlow, type NodeProps } from '@xyflow/react';
import Link from 'next/link';
import type { EditorView } from '@codemirror/view';
import { NodeFrame, type NodeFrameAction } from './node-frame';
import { fetchContentInfo } from '@/components/workspace/content-info-client';
import { announceContentRenamed } from '@/components/workspace/item-label';
import {
  VideoPreview,
  AudioPreview,
  Model3DPreview,
  FileCard,
  Lightbox,
} from '@/components/content-previews';
import {
  HtmlPreviewIframe,
  type WidgetLogEntry,
} from '@/components/content-previews/html-preview-iframe';
import { clampAutoFitSize } from '@/lib/workspaces/autosize';
import {
  appendWidgetLog,
  getWidgetRequestedFeatures,
  setWidgetLoadState,
  subscribeWidgetRequested,
} from '@/lib/workspaces/widget-log-buffer';
import {
  SENSITIVE_FEATURES,
  isSensitiveFeature,
  readGrantedPermissions,
} from '@/lib/workspaces/widget-permissions';
import {
  addLocalWidgetGrant,
  removeLocalWidgetGrant,
} from '@/lib/workspaces/widget-permissions-local';
import dynamic from 'next/dynamic';
// CodeMirror is the heaviest editor dependency. Load it only when a code/text
// file is actually opened, instead of shipping it in every workspace's initial
// bundle. Props-only (imperative access is via the `onViewReady` callback, not
// a ref), so `dynamic` needs no forwardRef shim. `ssr: false` — the live editor
// is client-only; the SSR twin doesn't need it.
const CodeEditor = dynamic(
  () => import('@/components/editors/code-editor').then((m) => m.CodeEditor),
  { ssr: false, loading: () => <div className="h-full w-full" /> },
);
import { VersionsPanel } from '@/components/content-editor/versions-panel';
import { ManifestHelper } from '@/components/content-editor/manifest-helper';
import {
  manifestErrorsToDiagnostics,
  setManifestDiagnostics,
  type ManifestError,
} from '@/lib/editors/manifest-lint';
import {
  useDesktopFullscreen,
  useWorkspace,
  useWorkspaceActions,
  useWorkspaceContent,
  useWorkspaceItem,
  useWorkspaceItems,
} from '@/components/workspace/store';
import { resolveWidget } from '@/lib/workspaces/widget-registry';
import { type WorkspaceItemState } from '@/lib/workspaces/types';
import { WidgetBridge } from '@/components/workspace/widget-bridge';
import { WidgetBootOverlay } from '@/components/workspace/widget-boot-overlay';
import { ExternalSourceEditor } from '@/components/workspace/content/external-source-editor';
import { DslView } from '@/components/workspace/dsl-view';
import type { DslSpec } from '@/lib/workspaces/widget-projection';
import { buttonVariants } from '@/components/ui/button';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/utils';
import { ShareDialog } from '@/components/sharing/share-dialog';
import { saveContentText, fetchContentText } from '@/lib/content/content-api';
// Import from `bundle-helpers` (NOT `bundle`) — see bundle.ts header
// for why. `bundle.ts` re-exports this symbol for server-side callers,
// but importing it here would drag drizzle/postgres into the client
// bundle and break the Turbopack build.
import { readBundleMetadata } from '@/lib/widgets/bundle-helpers';
import { REMIXABLE_KINDS } from '@/lib/widgets/bundle-types';
import { useWidgetRemixPrivate } from '@/hooks/use-widget-remix-private';
import { GradientPillButton } from '@/components/ui/gradient-pill-button';
import { RemixWithDialog, type PrivateCopy, type PrivateCopyError } from '@/components/workspace/remix-with-dialog';
import { REMIX_INTENT_EVENT, consumeRemixIntent } from '@/components/workspace/remix-intent';
import { useWidgetFreshness } from '@/components/workspace/use-widget-freshness';
import { DriveWithDialog, robutlerDrivePrefill } from '@/components/workspace/drive-with-dialog';
import { BackmixDialog } from '@/components/workspace/backmix-dialog';
import { resolveParticipant } from '@/lib/workspaces/resolve-participant';
import { openPublishAppDialog } from '@/components/workspace/publish-app-dialog';
import { appCategoryFor } from '@/lib/marketplace/categorize';
import { useAuth } from '@/components/auth-provider';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { fetchPersonalAgent, type PersonalAgent } from '@/hooks/use-personal-agent';

// Stable empty-KV reference for the projection-only branch below.
// Allocating `{}` inline on every render would change identity each
// time the parent re-renders (e.g. on drag) — `DslView` ignores
// prop-mirror updates when `liveSource` is set, but reusing the
// same object keeps React's reference-equality checks happy and
// avoids surfacing the same churn anywhere else.
const EMPTY_KV: Record<string, unknown> = Object.freeze({});

export type ContentNodeData = { itemId: string };

type ContentMeta = {
  id: string;
  displayName: string;
  /**
   * The server refused this id: it is no longer shared with the viewer, or it
   * was deleted. The node says so instead of drawing a file card whose Open
   * leads nowhere (2026-09-18, the Revent organizer scenario: an attendee's
   * conference window turned into a bare "Harbor Week" card, with no word on
   * why, the moment the organizer took the link back).
   */
  unavailable?: boolean;
  contentType: 'image' | 'video' | 'audio' | 'document' | 'model3d' | 'folder' | 'function' | 'widget';
  mimeType: string;
  rawText?: string | null;
  url?: string;
  previewUrl?: string | null;
  // Sharing + metadata fields surfaced by the settings overlay. These
  // come from the per-user `contentLinks` row joined onto the content
  // record (visibility/shareToken), plus a few base columns the
  // settings panel renders read-only (size, mime, updatedAt).
  linkVisibility?: 'private' | 'public' | 'unlisted' | null;
  shareToken?: string | null;
  /**
   * Inheritance-aware effective visibility (e.g. private blob in a
   * public workspace -> 'public'). Surfaced by `/api/content/[id]/info`
   * so the node header can show the right badge without re-running the
   * permissions walk on the client.
   */
  effectiveVisibility?: 'private' | 'public' | 'unlisted' | null;
  visibilitySource?: 'self' | 'folder' | 'workspace' | null;
  visibilitySourceName?: string | null;
  grantCount?: number | null;
  byteSize?: number | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  /**
   * Free-form metadata jsonb on the content row. Widget content uses
   * `metadata.widgetType` (resolved by `lib/workspaces/widget-registry.ts`)
   * to pick a render strategy — iframe entry, native node, or
   * projection-only renderer.
   */
  metadata?: Record<string, unknown> | null;
  /**
   * For a PROJECT FOLDER (ADR-0022): the editor widget's resolvable
   * metadata (`widgetType`/`bundle`/…), surfaced by `/info` so the canvas
   * mounts the app inline. Kept SEPARATE from `metadata` so the folder's
   * own (bundle-less) metadata still drives publish/remix gating — a
   * project instance is not itself a publishable widget.
   */
  openWithWidgetMeta?: Record<string, unknown> | null;
  /**
   * The handler widget's OWN content id (paired with `openWithWidgetMeta`).
   * Remix in the window header targets the APP — for a document window
   * that's this widget, never the document itself.
   */
  openWithWidgetId?: string | null;
  // ── Widget publish lifecycle (drives the title pill) ─────────────
  /** Content row creator — ownership gate for the Publish lifecycle. */
  creatorId?: string | null;
  /**
   * Server-resolved ownership for the current user: true when they are the
   * creator OR own the agent that authored it (seeded @robutler apps). Prefer
   * this over `creatorId === user.id`, which misses agent authorship.
   */
  ownedByMe?: boolean;
  /** Lineage default channel for the FIRST publish (remixed-from channel). */
  publishChannelSlug?: string | null;
  /** Set once the widget has been published (listed or unlisted). */
  publishedVersionId?: string | null;
  /** Set only for a LISTED publish (the post to open on "Published"). */
  publishedPostId?: string | null;
  /** True when the working folder changed since the last publish. */
  isDirty?: boolean | null;
  /** System-seeded catalog app (`widget://` storage) — never publishable. */
  system?: boolean;
  /**
   * Non-null when THIS row is a frozen published-version artifact with a
   * newer version: the successor artifact's content id. Drives the header
   * Update pill for version-pinned instances (marketplace spawns) — the
   * update writes `state.appVersionContentId`, never touches `refId`.
   */
  latestVersionContentId?: string | null;
};

type ContentView = 'preview' | 'source' | 'settings' | 'history';

function languageForMime(mime: string | undefined, name: string | undefined): string {
  if (!mime && !name) return 'markdown';
  const lower = (mime || '').toLowerCase();
  if (lower.includes('html')) return 'html';
  if (lower.includes('json')) return 'json';
  if (lower.includes('css')) return 'css';
  if (lower.includes('javascript')) return 'javascript';
  if (lower.includes('typescript')) return 'typescript';
  if (lower.includes('python')) return 'python';
  if (lower.includes('markdown')) return 'markdown';
  const ext = (name || '').split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'html': case 'htm': return 'html';
    case 'css': return 'css';
    case 'js': case 'mjs': case 'cjs': return 'javascript';
    case 'jsx': return 'jsx';
    case 'ts': return 'typescript';
    case 'tsx': return 'tsx';
    case 'py': return 'python';
    case 'json': return 'json';
    case 'md': case 'markdown': return 'markdown';
    default: return 'markdown';
  }
}

function isText(mime: string): boolean {
  return mime.startsWith('text/') || mime === 'application/json' || mime === 'application/javascript';
}

function isHtml(mime: string, displayName?: string): boolean {
  if (mime.toLowerCase().includes('html')) return true;
  return /\.html?$/i.test(displayName ?? '');
}

/**
 * Reactive read of the per-item "requested but ungranted feature"
 * signal (header shield). Backed by the ephemeral registry in
 * `widget-log-buffer`; re-renders when `widget-bridge` marks a new
 * request. Returns a stable comma-joined snapshot so
 * `useSyncExternalStore` doesn't loop on a fresh array each tick.
 */
function useRequestedFeatures(itemId: string): string[] {
  const sig = useSyncExternalStore(
    useCallback((cb) => subscribeWidgetRequested(itemId, cb), [itemId]),
    () => getWidgetRequestedFeatures(itemId).join(','),
    () => '',
  );
  return useMemo(() => (sig ? sig.split(',') : []), [sig]);
}

function ContentNodeImpl({ data }: NodeProps) {
  const t = useT();
  const itemId = (data as ContentNodeData).itemId;
  const item = useWorkspaceItem(itemId);
  const workspace = useWorkspace();
  const { patchItemState, seedContent, addItem, bringToFront } = useWorkspaceActions();
  const flow = useReactFlow();
  // Header shield: features the widget asked for that the user hasn't
  // granted yet. Drives a one-click "open Settings to grant" affordance.
  const requestedFeatures = useRequestedFeatures(itemId);
  const grantedFeatures = useMemo(
    () => readGrantedPermissions(item?.state),
    [item?.state],
  );
  const pendingGrants = useMemo(
    () => requestedFeatures.filter((f) => !grantedFeatures.includes(f)),
    [requestedFeatures, grantedFeatures],
  );

  const refId = item?.refId ?? null;
  const cache = (item?.state.cache ?? {}) as ContentMeta & Record<string, unknown>;
  const persistedView = (item?.state.view as ContentView | undefined) ?? 'preview';
  // Local-only override for the "Edit URL" toggle on iframe-external
  // widgets. We deliberately DON'T persist this — when the user
  // refreshes / re-opens the workspace, they should land back on the
  // embed (preview), not on an editor surface. HTML / markdown
  // widgets keep their `view` persisted because their source mode
  // hosts real editable content the user expects to come back to.
  const [externalEditOpen, setExternalEditOpen] = useState(false);
  // What the rest of the render logic uses — equals the persisted
  // view EXCEPT for external iframes, where the source toggle is
  // session-local.
  const view: ContentView = externalEditOpen ? 'source' : persistedView;
  // Server-hydrated metadata for this content id, seeded by
  // getWorkspaceForUser. When present, we paint immediately on mount
  // without showing the "Loading…" placeholder; the network round-trip
  // still happens in the background so per-user fields (sharing token,
  // visibility) populate as soon as they're available.
  const cachedMeta = useWorkspaceContent(refId);

  // Double-click on the header used to zoom the CAMERA onto the widget
  // (fitView). The canvas is a fixed-zoom surface now, so the gesture
  // flips direction: it resizes the APP — grow to best-fit the visible
  // viewport (clamped to the app's declared maxSize, centered in view),
  // and a second double-click restores the pre-maximize rect stashed in
  // `cache.preMaxRect`. A window-manager maximize/restore, not a camera
  // move.
  const toggleMaximizeToViewport = useCallback(() => {
    const st = item?.state;
    if (!st) return;
    const stCache = (st.cache ?? {}) as Record<string, unknown>;
    const stash = stCache.preMaxRect as
      | { x: number; y: number; width: number; height: number }
      | null
      | undefined;
    if (
      stash &&
      typeof stash.width === 'number' &&
      typeof stash.height === 'number'
    ) {
      patchItemState(itemId, {
        x: stash.x,
        y: stash.y,
        width: stash.width,
        height: stash.height,
        // patchItemState MERGES `cache`, so omitting the key would leave
        // the stale stash behind (and every later double-click would
        // "restore" to where the window already is). Explicit null is
        // the delete.
        cache: { preMaxRect: null } as WorkspaceItemState['cache'],
      });
      return;
    }
    // Visible viewport in world coords, inset by a margin plus the
    // reserved rail column so the maximized frame doesn't slide under
    // the chrome. screenToFlowPosition keeps this correct at any camera
    // position (zoom is fixed, but not necessarily exactly 1).
    const railW =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--ws-rail-w',
        ),
      ) || 0;
    // Same uniform 8px spacing as the tiling layouts (Part XII) — the
    // maximized window and a tiled pair read as one system.
    const MARGIN = 8;
    const tl = flow.screenToFlowPosition({ x: railW + MARGIN, y: MARGIN });
    const br = flow.screenToFlowPosition({
      x: window.innerWidth - MARGIN,
      y: window.innerHeight - MARGIN,
    });
    const decl = stCache as { maxSize?: { width: number; height: number } };
    let width = Math.max(320, br.x - tl.x);
    let height = Math.max(240, br.y - tl.y);
    if (decl.maxSize) {
      width = Math.min(width, decl.maxSize.width);
      height = Math.min(height, decl.maxSize.height);
    }
    bringToFront(itemId);
    patchItemState(itemId, {
      // Center the (possibly maxSize-clamped) rect in the viewport.
      x: tl.x + (br.x - tl.x - width) / 2,
      y: tl.y + (br.y - tl.y - height) / 2,
      width,
      height,
      // Merge semantics: only the stash key needs to ride the patch.
      cache: {
        preMaxRect: { x: st.x, y: st.y, width: st.width, height: st.height },
      } as WorkspaceItemState['cache'],
    });
  }, [item, itemId, flow, patchItemState, bringToFront]);

  const [meta, setMeta] = useState<ContentMeta | null>(() => {
    if (!cachedMeta) return null;
    return {
      id: cachedMeta.id,
      displayName: cachedMeta.displayName,
      contentType: cachedMeta.contentType as ContentMeta['contentType'],
      mimeType: cachedMeta.mimeType,
      url: `/api/content/${cachedMeta.id}`,
      previewUrl: cachedMeta.thumbnailPath ? `/api/content/${cachedMeta.id}/preview` : null,
      byteSize: cachedMeta.byteSize,
      updatedAt: cachedMeta.updatedAt,
    };
  });
  // A rename made elsewhere in this tab (the Content panel, an app's Manage
  // drawer, an app's own title field) reaches this tile only through the live
  // content cache (components/workspace/item-label.ts). `meta` is seeded from
  // that cache once and otherwise refreshed only by this tile's own `/info`
  // and settings, so after such a rename (2026-09-13 review) the rail showed
  // the new name while this window's header, its settings Name field and its
  // Publish and Drive dialogs kept the old one, and typing the old name back
  // into the Name field was dropped as "unchanged". Fold the live name in
  // whenever it changes. Per-tab React state only: no item or doc write.
  const liveContentId = cachedMeta?.id;
  const liveDisplayName = cachedMeta?.displayName;
  useEffect(() => {
    if (typeof liveDisplayName !== 'string' || liveDisplayName.trim() === '') return;
    // Same row only: a tile rebound to another refId keeps its meta until its
    // own `/info` for the new row lands.
    setMeta((m) =>
      m && m.id === liveContentId && m.displayName !== liveDisplayName
        ? { ...m, displayName: liveDisplayName }
        : m,
    );
  }, [liveContentId, liveDisplayName]);
  const [lightbox, setLightbox] = useState(false);
  // "Open in" pin (state.openWith): the handler widget the user chose for a
  // plain FILE ref. Written once at add time by the picker, so reading it
  // through the effect closures below can never go stale.
  const openWithPin = item?.state?.openWith ?? null;
  // ShareDialog open/close — owned here so the Share frame action and
  // the badge in the header can both surface the same modal without
  // prop-drilling. Closed by default; opened by Share action click.
  const [shareOpen, setShareOpen] = useState(false);

  // Text editing state. `draftText` is what the editor displays;
  // `savedText` tracks the last value successfully persisted to the
  // server so we can render a "Saved/Saving" indicator and short-
  // circuit no-op saves. `null` for `draftText` means we haven't
  // loaded the file yet — distinguished from an empty file ('').
  const [draftText, setDraftText] = useState<string | null>(null);
  const [savedText, setSavedText] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const savedFadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Publish-as-widget — converts an HTML document into a widget post on
  // the marketplace. Status drives the action button's label so the
  // user sees in-flight / success feedback inline.
  const [publishStatus, setPublishStatus] = useState<'idle' | 'publishing' | 'published' | 'error'>('idle');
  const publishFadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [publishedPostId, setPublishedPostId] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydrate metadata once we have a refId. Fall back to cache for first paint.
  useEffect(() => {
    let alive = true;
    if (!refId) {
      setMeta(null);
      return;
    }
    // Inline-payload short-circuit: `present.url` / `present.widget`
    // and anything else the agent surfaces without a backing content
    // row uses `refId = "inline:<uuid>"` and carries the payload on
    // `cache.payload`. There's no content API to fetch from — paint
    // directly from cache. The widget body's mime-keyed renderer map
    // handles the actual visual via `cache.payload`.
    if (refId.startsWith('inline:')) {
      const inlineMime =
        (cache.mimeType as string | undefined) ?? 'application/octet-stream';
      setMeta({
        id: refId,
        displayName: (cache.displayName as string) ?? 'Content',
        contentType:
          (cache.contentType as ContentMeta['contentType']) ?? 'document',
        mimeType: inlineMime,
        url: '',
        previewUrl: null,
        metadata:
          (cache.payload as Record<string, unknown> | undefined) ?? null,
      });
      return () => {
        alive = false;
      };
    }
    // `/api/content/[id]` serves the binary file. The `/info` sibling
    // returns the JSON metadata we actually need here (displayName,
    // mime, sharing grant for the current user, etc.). For text-like
    // content we ALSO need the raw bytes for the source/edit view —
    // fetched lazily by the body when the user flips the view.
    // Pull the raw bytes for text/html content. Extracted into a helper
    // so BOTH the /info-success path and the /info-FAILURE fallback can
    // call it. Previously the body fetch lived ONLY inside the success
    // branch, so a transient `/info` non-2xx (a dev-server restart / 502,
    // or the earlier `text=uuid` 500) left an HTML preview node
    // permanently blank: meta fell back to cache but the body was never
    // loaded and nothing retried. We now load the body independently and
    // retry a few times on transient failure so the tile self-heals once
    // the server recovers.
    let bodyAttempts = 0;
    const loadBody = () => {
      fetch(`/api/content/${refId}`)
        .then((r) => {
          if (!r.ok) throw new Error(`content ${r.status}`);
          return r.text();
        })
        .then((text) => {
          if (!alive) return;
          setMeta((m) => (m ? { ...m, rawText: text } : m));
          // Seed the editor on first load only — re-seeding on a later
          // refetch would clobber unsaved edits, so only write when
          // `draftText` is still null (never loaded).
          setDraftText((prev) => (prev == null ? text : prev));
          setSavedText((prev) => (prev == null ? text : prev));
        })
        .catch(() => {
          if (!alive || bodyAttempts >= 3) return;
          bodyAttempts += 1;
          setTimeout(() => {
            if (alive) loadBody();
          }, 800 * bodyAttempts);
        });
    };

    // Coalesced: every ContentNode mounting in the same tick shares ONE
    // `POST /api/content/info-batch` round-trip (content-info-client.ts).
    // Rejects exactly where the old per-node fetch threw on non-2xx, so the
    // catch fallback below is unchanged.
    fetchContentInfo(refId)
      .then((data: Record<string, any>) => {
        if (!alive || !data) return;
        const next: ContentMeta = {
          id: data.id,
          displayName: data.displayName ?? cache.displayName ?? 'Content',
          contentType: data.contentType ?? cache.contentType ?? 'document',
          mimeType: data.mimeType ?? cache.mimeType ?? 'application/octet-stream',
          rawText: null,
          url: data.url ?? `/api/content/${refId}`,
          previewUrl: data.previewUrl ?? null,
          linkVisibility: data.linkVisibility ?? null,
          shareToken: data.shareToken ?? null,
          effectiveVisibility: data.effectiveVisibility ?? data.linkVisibility ?? null,
          visibilitySource: data.visibilitySource ?? 'self',
          visibilitySourceName: data.visibilitySourceName ?? null,
          grantCount: data.grantCount ?? 0,
          byteSize: data.byteSize ?? data.size ?? null,
          updatedAt: data.updatedAt ?? null,
          createdAt: data.createdAt ?? null,
          metadata: data.metadata ?? null,
          openWithWidgetMeta: data.openWithWidgetMeta ?? null,
          openWithWidgetId: data.openWithWidgetId ?? null,
          creatorId: data.creatorId ?? null,
          ownedByMe: data.ownedByMe ?? false,
          publishChannelSlug: data.publishChannelSlug ?? null,
          publishedVersionId: data.publishedVersionId ?? null,
          publishedPostId: data.publishedPostId ?? null,
          isDirty: data.isDirty ?? false,
          system: data.system ?? false,
        };
        // A pinned open-with app (state.openWith) is resolved by its own
        // effect below; `/info` only surfaces handlers for project folders,
        // so carry an already-resolved pin across instead of nulling it.
        setMeta((prev) =>
          !next.openWithWidgetMeta && openWithPin && prev?.openWithWidgetId === openWithPin
            ? { ...next, openWithWidgetMeta: prev.openWithWidgetMeta, openWithWidgetId: prev.openWithWidgetId }
            : next,
        );
        // Refresh the workspace-wide cache so a future widget for the
        // same content id can paint instantly without its own info
        // round-trip.
        seedContent({
          id: next.id,
          displayName: next.displayName,
          contentType: next.contentType,
          mimeType: next.mimeType,
          thumbnailPath: data.thumbnailPath ?? null,
          byteSize: next.byteSize ?? null,
          updatedAt: next.updatedAt ?? null,
        });
        if (isText(next.mimeType) || isHtml(next.mimeType, next.displayName)) {
          loadBody();
        }
      })
      .catch((err: unknown) => {
        // /info failed (network error OR non-2xx). Fall back to the
        // workspace-item cache so the node still renders — the cached
        // displayName alone is enough for `isHtml` to choose the HTML
        // preview path — and load the body so the preview isn't stuck
        // blank waiting on a metadata round-trip that may never succeed.
        if (!alive) return;
        const fbName = (cache.displayName as string) ?? 'Content';
        const fbMime = (cache.mimeType as string) ?? 'application/octet-stream';
        // A REFUSED id is not a network blip: the batch answers null for an id
        // the viewer may not read or that is gone ("info 404"), the single
        // GET its status. Say so rather than draw a card that opens nothing.
        // An answer that said nothing about the id ("info unanswered") is not
        // a refusal and keeps the cache fallback.
        const unavailable = err instanceof Error && /^info 40[34]$/.test(err.message);
        setMeta({
          id: refId,
          displayName: fbName,
          contentType: (cache.contentType as ContentMeta['contentType']) ?? 'document',
          mimeType: fbMime,
          url: `/api/content/${refId}`,
          previewUrl: cache.thumbnailPath ? `/api/content/${refId}/preview` : null,
          unavailable,
        });
        if (!unavailable && (isText(fbMime) || isHtml(fbMime, fbName))) {
          loadBody();
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  // Live reload on agent edit. When the chat agent calls
  // `text_editor str_replace / insert / undo_edit` on the SAME
  // contentId this node is rendering, chat-view fires a
  // `workspace:content-updated` window CustomEvent (see
  // components/chat/chat-view.tsx). We refetch the body and reseed
  // the draft so the HTML iframe / code editor re-renders with the
  // new code — without requiring a page reload. The agent's
  // text_editor handlers also append a new content_versions row
  // (lib/llm/platform-tool-handlers.ts), so the version-history
  // UI shows the edit immediately.
  //
  // Safety: if the user has unsaved local edits (`draftText !==
  // savedText`), we DO NOT clobber them — the user's edits win.
  // That mirrors how a CodeMirror buffer with unsaved diffs survives
  // an external file mutation.
  useEffect(() => {
    if (!refId) return;
    if (typeof window === 'undefined') return;
    // For Plan v3-05 folder-bundle widgets, the workspace_item refId
    // points at the folder content row — so an edit to any CHILD of
    // that folder (e.g. `widget.json`, `tools/foo.js`) should trigger
    // a reload here too. We accept events where EITHER the edited
    // contentId matches our refId OR the edited file's parentFolderId
    // matches our refId.
    //
    // For legacy single-file widgets (refId points at an HTML doc),
    // only the contentId match path fires.
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<{ contentId: string; parentFolderId?: string | null }>)
        .detail;
      if (!detail) return;
      const directHit = detail.contentId === refId;
      const folderHit = !!detail.parentFolderId && detail.parentFolderId === refId;
      if (!directHit && !folderHit) return;
      fetch(`/api/content/${refId}`)
        .then((r) => (r.ok ? r.text() : null))
        .then((text) => {
          if (text == null) return;
          setMeta((m) => (m ? { ...m, rawText: text } : m));
          // Reseed only when there are no unsaved local edits — keep
          // the user's CodeMirror buffer if they were editing.
          setDraftText((prev) => (prev == null || prev === savedText ? text : prev));
          setSavedText(text);
        })
        .catch(() => {});
    };
    window.addEventListener('workspace:content-updated', handler as EventListener);
    return () => {
      window.removeEventListener('workspace:content-updated', handler as EventListener);
    };
  }, [refId, savedText]);

  const isHtmlContent = useMemo(
    () => meta && isHtml(meta.mimeType, meta.displayName),
    [meta],
  );
  const isTextLike = useMemo(
    () => meta && (isText(meta.mimeType) || !!isHtmlContent),
    [meta, isHtmlContent],
  );
  // Markdown is the only NON-HTML text type with a meaningful
  // rendered ↔ source split: CodeEditor's `richMode` extension paints
  // headings / bold / lists / links inline while keeping the buffer
  // editable. For markdown we treat `view === 'preview'` (the default)
  // as rich and `view === 'source'` as raw — mirrors the /content
  // page's Rich / Raw toggle (see `app/(portal)/(main)/content/[id]/page.tsx`
  // around line 495). Other text languages have no preview to flip to.
  const isMarkdownContent = useMemo(() => {
    if (!meta) return false;
    return languageForMime(meta.mimeType, meta.displayName) === 'markdown';
  }, [meta]);

  // ---- text save ---------------------------------------------------------
  // Persist the editor's current draft. Idempotent: returns immediately if
  // nothing is dirty or a save is already in flight. The Saved status
  // briefly visible afterwards is purely cosmetic and fades on a timer.
  const doSave = useCallback(async () => {
    if (!refId || draftText == null) return;
    if (savedText !== null && draftText === savedText) return;
    if (saveStatus === 'saving') return;
    setSaveStatus('saving');
    try {
      await saveContentText(refId, draftText);
      setSavedText(draftText);
      setSaveStatus('saved');
      if (savedFadeTimer.current) clearTimeout(savedFadeTimer.current);
      savedFadeTimer.current = setTimeout(() => setSaveStatus('idle'), 1500);
      // Notify any open VersionsPanel so the timeline refreshes in
      // real time after the new version row lands. Matches the same
      // event /content/[id] page dispatches after `doSave`.
      window.dispatchEvent(
        new CustomEvent('content-saved', { detail: { id: refId } }),
      );
    } catch {
      setSaveStatus('error');
    }
  }, [refId, draftText, savedText, saveStatus]);

  // ---- validate (function content) ---------------------------------------
  // For Robutler functions (contentType === 'function'), validate the
  // current draft against the binding endpoint and paint manifest
  // diagnostics into the CodeMirror gutter. Mirrors what the full
  // /content/[id] editor does; the widget action is just a more
  // discoverable trigger than the manifest-helper inline panel.
  const editorViewRef = useRef<EditorView | null>(null);
  const [validateStatus, setValidateStatus] = useState<
    'idle' | 'validating' | 'ok' | 'errors'
  >('idle');
  const [errorCount, setErrorCount] = useState<number>(0);
  const validateFadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doValidate = useCallback(async () => {
    if (!refId || draftText == null) return;
    if (validateStatus === 'validating') return;
    setValidateStatus('validating');
    try {
      // Save first if dirty: the binding endpoint validates the
      // SOURCE in the request body, but a user clicking Validate
      // generally also wants their current edits persisted.
      if (savedText !== null && draftText !== savedText) {
        await doSave();
      }
      const r = await fetch(`/api/content/${refId}/function-binding`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ source: draftText }),
      });
      const d = (await r.json()) as { ok: boolean; errors?: ManifestError[] };
      const errors = d.errors ?? [];
      setErrorCount(errors.length);
      const view = editorViewRef.current;
      if (view) {
        setManifestDiagnostics(
          view,
          manifestErrorsToDiagnostics(draftText, errors),
        );
      }
      setValidateStatus(errors.length === 0 ? 'ok' : 'errors');
      // Auto-fade the "ok" pill after a beat; errors stay sticky
      // until the user re-validates so the diagnostics remain
      // visually associated with the action they triggered.
      if (validateFadeTimer.current) clearTimeout(validateFadeTimer.current);
      if (errors.length === 0) {
        validateFadeTimer.current = setTimeout(
          () => setValidateStatus('idle'),
          2200,
        );
      }
    } catch {
      setValidateStatus('errors');
      setErrorCount(0);
    }
  }, [refId, draftText, savedText, validateStatus, doSave]);

  // Cleanup validate fade timer on unmount.
  useEffect(
    () => () => {
      if (validateFadeTimer.current) clearTimeout(validateFadeTimer.current);
    },
    [],
  );

  // Reload the text body after a version restore so the editor shows
  // the freshly-restored bytes. VersionsPanel's `onRestored` fires
  // post-restore — we re-fetch the same endpoint the hydration effect
  // uses so the editor and `savedText` baseline stay in sync.
  const reloadText = useCallback(async () => {
    if (!refId) return;
    try {
      const text = await fetchContentText(refId);
      setDraftText(text);
      setSavedText(text);
      setSaveStatus('idle');
      setMeta((m) => (m ? { ...m, rawText: text } : m));
    } catch {
      /* ignored — user can hit refresh */
    }
  }, [refId]);

  // Debounced auto-save. Workspace widgets are tiny and users may not
  // know about Cmd+S; idle-debounce of ~900ms strikes a balance between
  // responsiveness and not hammering the API on every keystroke.
  useEffect(() => {
    if (!isTextLike) return;
    if (draftText == null || savedText == null) return;
    if (draftText === savedText) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      void doSave();
    }, 900);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [draftText, savedText, isTextLike, doSave]);

  // Cleanup on unmount.
  useEffect(
    () => () => {
      if (savedFadeTimer.current) clearTimeout(savedFadeTimer.current);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    [],
  );

  const dirty = savedText !== null && draftText !== null && draftText !== savedText;

  // Publish-as-widget — POST to the new route with the current canvas
  // size so the inferred WidgetMeta lands at the size the user already
  // sized this node to (their "widget dimensions"). Owner check is
  // enforced server-side; we deliberately don't pre-gate the button on
  // creatorId because the node doesn't carry that info today — the
  // server's 403 surfaces as an inline Failed status without
  // crashing the node.
  const doPublishAsWidget = useCallback(async () => {
    if (!refId || !item) return;
    if (publishStatus === 'publishing') return;
    setPublishStatus('publishing');
    try {
      const res = await fetch(`/api/content/${refId}/publish-as-widget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          size: {
            width: Math.round(item.state.width),
            height: Math.round(item.state.height),
          },
        }),
      });
      if (!res.ok) {
        setPublishStatus('error');
        if (publishFadeTimer.current) clearTimeout(publishFadeTimer.current);
        publishFadeTimer.current = setTimeout(() => setPublishStatus('idle'), 2500);
        return;
      }
      const data = (await res.json()) as { postId?: string };
      if (data?.postId) setPublishedPostId(data.postId);
      setPublishStatus('published');
      if (publishFadeTimer.current) clearTimeout(publishFadeTimer.current);
      // Keep the success state up a bit longer so the user has time to
      // click the post link before the button reverts to "Publish".
      publishFadeTimer.current = setTimeout(() => setPublishStatus('idle'), 4000);
    } catch {
      setPublishStatus('error');
      if (publishFadeTimer.current) clearTimeout(publishFadeTimer.current);
      publishFadeTimer.current = setTimeout(() => setPublishStatus('idle'), 2500);
    }
  }, [refId, item, publishStatus]);
  // Widgets can be remixed only when the bundle kind is `folder` or
  // `external-cooperative`. Native / system / external-passive widgets
  // are intentionally excluded. We pull the bundle through the
  // backward-compat reader so legacy `metadata.iframeEntry` rows still
  // resolve to a `folder` bundle.
  const bundle = useMemo(
    () => readBundleMetadata({ metadata: (meta?.metadata ?? null) as Record<string, unknown> | null }),
    [meta?.metadata],
  );
  // Remix ALWAYS targets the APP — this is the app's window. An app item
  // carries its own bundle; a DOCUMENT window resolves the app through its
  // open-with handler widget (openWithWidgetMeta/-Id), so remixing from an
  // open document still clones the EDITOR app, never the document (documents
  // copy via File → Save a copy).
  const openWithBundle = useMemo(
    () => readBundleMetadata({ metadata: (meta?.openWithWidgetMeta ?? null) as Record<string, unknown> | null }),
    [meta?.openWithWidgetMeta],
  );
  const appBundle = bundle ?? openWithBundle;
  const remixTargetId = bundle ? refId : (openWithBundle ? (meta?.openWithWidgetId ?? null) : null);
  // Document window: the viewer may only hold a grant on the DOCUMENT, not on
  // the (possibly private) app opening it — the doc rides along as the
  // server-verified remix delegation (`via`).
  const remixVia = !bundle && openWithBundle && refId ? refId : null;
  const isRemixable =
    !!appBundle && (REMIXABLE_KINDS as readonly string[]).includes(appBundle.kind) && !!remixTargetId;
  const isExternalCooperative = bundle?.kind === 'external-cooperative';

  const { remix: remixPrivate, loading: remixLoading } = useWidgetRemixPrivate();
  const [backingAgentOpen, setBackingAgentOpen] = useState(false);
  const [remixDialogOpen, setRemixDialogOpen] = useState(false);
  // A store page / Discover "Remix" CTA spawns the app and asks this node to
  // open the dialog: consume the one-shot intent on mount (the node did not
  // exist when it was raised) and listen for later ones.
  useEffect(() => {
    if (!item || !isRemixable) return;
    if (consumeRemixIntent(item.id)) setRemixDialogOpen(true);
    const onIntent = (e: Event) => {
      const itemId = (e as CustomEvent<{ itemId?: string }>).detail?.itemId;
      if (itemId === item.id && consumeRemixIntent(item.id)) setRemixDialogOpen(true);
    };
    window.addEventListener(REMIX_INTENT_EVENT, onIntent);
    return () => window.removeEventListener(REMIX_INTENT_EVENT, onIntent);
  }, [item, isRemixable]);
  const [driveDialogOpen, setDriveDialogOpen] = useState(false);
  // A task the APP proposed via host.ui.driveWith — appended to the drive
  // prompt so the agent starts on what the person was actually stuck on.
  const [driveTask, setDriveTask] = useState<string | null>(null);
  // Split-CTA menu: the single "AI" header pill groups Drive / Remix /
  // Publish. Holds the anchor rect (portal-positioned below the pill).
  const [aiMenuAt, setAiMenuAt] = useState<{ top: number; right: number } | null>(null);
  const aiPillRef = useRef<HTMLSpanElement | null>(null);
  // ≥300px the header shows Remix/Drive as their own buttons (pair or
  // segmented pill) — the dropdown then only carries the Publish lifecycle.
  const splitCta = !!item && item.state.width >= 300;
  // Long-lived tabs keep running the widget bundle they mounted with — a
  // reseed/republish never reaches an open iframe on its own. Watch the
  // catalog row's updatedAt (batched, focus-throttled) and offer an in-place
  // update; bumping the nonce re-keys the bridge, which rebuilds the iframe
  // against the no-cache bundle routes = guaranteed fresh code.
  //
  // VERSION-PINNED instances (marketplace spawns) reference an IMMUTABLE
  // published-version row — its updatedAt never changes, so their signal is
  // `latestVersionContentId` (the successor artifact, server-advertised on
  // /info and the freshness poll). Their Update writes
  // `state.appVersionContentId`, which overrides WHICH ROW SUPPLIES THE CODE
  // below — identity (refId → rooms, KV scope, grants) never moves.
  const [appUpdateNonce, setAppUpdateNonce] = useState(0);
  const hasFolderBundle =
    (meta?.metadata?.bundle as { kind?: string } | undefined)?.kind === 'folder';
  const { stale: appUpdateAvailable, latest: polledLatest, markFresh: markAppFresh } =
    useWidgetFreshness(
      meta && (typeof meta.metadata?.widgetType === 'string' || hasFolderBundle)
        ? meta.id
        : null,
    );
  const appVersionOverride = item?.state?.appVersionContentId ?? null;
  const successorId = polledLatest ?? meta?.latestVersionContentId ?? null;
  const versionUpdateAvailable = !!successorId && successorId !== appVersionOverride;
  // The override row's metadata, fetched for the spec resolution below.
  // `forId` records which override the fetch settled for; `metadata: null`
  // means the successor row was unreadable (revoked/pruned) and the spec
  // falls back to the pinned version instead of staying blank.
  const [overrideMeta, setOverrideMeta] = useState<{
    forId: string;
    metadata: Record<string, unknown> | null;
  } | null>(null);
  useEffect(() => {
    if (!appVersionOverride) {
      setOverrideMeta(null);
      return;
    }
    let alive = true;
    void (async () => {
      let fetched: Record<string, unknown> | null = null;
      try {
        const r = await fetch(`/api/content/${encodeURIComponent(appVersionOverride)}/info`, {
          credentials: 'same-origin',
        });
        if (r.ok) {
          const j = (await r.json()) as { metadata?: Record<string, unknown> | null };
          fetched = j.metadata ?? {};
        }
      } catch {
        // fall through — settle as unreadable
      }
      if (alive) setOverrideMeta({ forId: appVersionOverride, metadata: fetched });
    })();
    return () => {
      alive = false;
    };
  }, [appVersionOverride]);

  // "Open in" pin — resolve the chosen handler widget's metadata and graft it
  // onto `meta` as openWithWidgetMeta/-Id, so a pinned FILE mounts its app
  // through the exact same path as a project folder (spec resolution, app
  // title, remix target, edit gating all read those two fields). `/info`
  // resolves open-with for folders only, so files carry the pin client-side.
  // Re-runs once when `meta` first settles (the guard below can't write into
  // a null meta); refetches hit the SW/browser cache.
  const hasMeta = !!meta;
  useEffect(() => {
    if (!openWithPin || !refId || refId.startsWith('inline:') || !hasMeta) return;
    let alive = true;
    void (async () => {
      try {
        const r = await fetch(`/api/content/${encodeURIComponent(openWithPin)}/info`, {
          credentials: 'same-origin',
        });
        if (!r.ok) return; // unreadable pin → keep the native renderer
        const j = (await r.json()) as { metadata?: Record<string, unknown> | null };
        if (!alive || !j?.metadata) return;
        setMeta((m) =>
          m && !m.openWithWidgetMeta
            ? { ...m, openWithWidgetMeta: j.metadata ?? null, openWithWidgetId: openWithPin }
            : m,
        );
      } catch {
        /* keep the native renderer */
      }
    })();
    return () => {
      alive = false;
    };
  }, [openWithPin, refId, hasMeta]);
  const [backmixOpen, setBackmixOpen] = useState(false);
  const { openHref } = useFullscreenNav();
  // Inline error strip for the in-workspace Remix path. The Remix dialog
  // closes itself the moment it hands off to `doRemix`, so anything that
  // goes wrong after that has to be shown on the node itself.
  const [remixNotice, setRemixNotice] = useState<string | null>(null);

  // In-workspace Remix: make an OWNED, private copy of this widget, drop
  // it on the canvas, and open the caller's OWN personal agent prefilled
  // to modify it. No public post (the user "Publish as widget" later) and
  // no navigation away.
  //
  // ORDER MATTERS. The agent is resolved BEFORE `remixPrivate` writes the
  // copy: the write is the irreversible step (a new owned row in My
  // Library), so a failed resolution must exit before it, never after it,
  // or the user is left with an orphan private copy and no chat to edit it
  // in. Until 2026-09-05 this opened @robutler.factory after the write and
  // swallowed the failure with a console.warn; the factory is retired (the
  // personal agent carries `build_apps` itself) and the handle comes from
  // `GET /api/agents/personal`, never `${username}.my-robutler`.
  const doRemix = useCallback(async () => {
    if (!remixTargetId || remixLoading) return;
    setRemixNotice(null);
    let agent: PersonalAgent;
    try {
      const lookup = await fetchPersonalAgent();
      if (!lookup.agent) {
        if (lookup.guest) {
          openHref('/signup');
          return;
        }
        setRemixNotice(t('personalAgent.notReady'));
        return;
      }
      agent = lookup.agent;
    } catch {
      setRemixNotice(t('personalAgent.openFailed'));
      return;
    }
    const result = await remixPrivate(remixTargetId, { via: remixVia });
    if (!result.ok) {
      setRemixNotice(result.message);
      return;
    }
    // 1) Drop the private copy on the canvas, sized like this widget.
    await addItem({
      type: 'content',
      refId: result.contentId,
      state: item
        ? { width: Math.round(item.state.width), height: Math.round(item.state.height) }
        : {},
    });
    // 2) Open the personal agent prefilled to tweak the copy. (`addItem`
    //    already fires `emitWorkspaceItemsChanged`, refreshing My Library.)
    //    The prefill is the PERSON's message, so it is short and says only
    //    what they would say: which copy, and what to change. Until
    //    2026-09-30 it carried tool names (widget_download, widget_put_files)
    //    as instructions to the agent; the agent's own build-apps skill
    //    carries its instructions, including the small-change scope rule.
    try {
      await addItem({
        type: 'agent',
        refId: agent.id,
        state: {
          width: 450,
          height: 500,
          cache: {
            username: agent.username,
            displayName: agent.displayName ?? agent.username,
            avatarUrl: agent.avatarUrl ?? null,
            prefillText: `Let's remix "${result.displayName}". It's my private copy${result.folderId ? ` (app folder ${result.folderId})` : ''}. I'd like to change: `,
          },
        },
      });
    } catch {
      setRemixNotice(t('personalAgent.openFailed'));
    }
  }, [remixTargetId, remixVia, remixLoading, remixPrivate, addItem, item, openHref, t]);

  // For "Remix with <external env>": make the same owned private copy, but instead of
  // spawning the personal agent, return its ids so the dialog can hand them to the chosen tool.
  const createPrivateCopy = useCallback(async (): Promise<PrivateCopy | PrivateCopyError | null> => {
    if (!remixTargetId) return null;
    const result = await remixPrivate(remixTargetId, { via: remixVia });
    if (!result.ok) return result.message ? { error: result.message } : null;
    // Drop the copy on THIS canvas so the chosen agent has a live, accessible
    // instance to drive + inspect (workspace_widgets_*). Commands route to a
    // live tab, so once the user keeps this workspace open the agent can
    // screenshot / read logs / invoke against it. Best-effort: the remix
    // prompt still works without the instance handle.
    let workspaceId: string | undefined;
    let itemId: string | undefined;
    try {
      const created = await addItem({
        type: 'content',
        refId: result.contentId,
        state: item
          ? { width: Math.round(item.state.width), height: Math.round(item.state.height) }
          : {},
      });
      if (created) {
        workspaceId = workspace.id;
        itemId = created.id;
      }
    } catch (err) {
      console.warn('[content-node] instantiate after remix failed', err);
    }
    return {
      contentId: result.contentId,
      folderId: result.folderId,
      displayName: result.displayName,
      workspaceId,
      itemId,
      canPublish: result.canPublish,
    };
  }, [remixTargetId, remixVia, remixPrivate, addItem, item, workspace.id]);

  // ── Drive (agent command surface) ─────────────────────────────────
  // Remix hands an AI the app's FILES; Drive hands it the CONTROLS. Shown for
  // any live iframe app that declares an agent command interface (registry
  // spec for first-party, bundle manifest for remixes) — the dialog fires a
  // coding-tool deep-link with a ready drive prompt, or drops a Robutler
  // agent prefilled to drive it here.
  // (isDrivable itself is computed below, after `widgetSpec` resolves.)
  // Robutler option: drop the assistant on the canvas prefilled to drive this
  // app (same pattern as the factory spawn after an in-app remix).
  /* Answers whether the agent actually landed, so the dialog can stay open and
     say so when it did not (2026-09-16: three timed-out mounts in a row on a
     loaded server left the person with no new agent, no message, and their
     next sentence going to whichever chat was underneath). */
  const doDriveWithRobutler = useCallback(async (agent?: { id: string; username: string; displayName?: string | null; avatarUrl?: string | null }): Promise<boolean> => {
    try {
      // The picker hands us the chosen agent; no pick = the default assistant.
      const assistant = agent ?? await resolveParticipant('robutler');
      if (!assistant?.id) return false;
      await addItem({
        type: 'agent',
        refId: assistant.id,
        state: {
          width: 450,
          height: 500,
          cache: {
            username: assistant.username,
            displayName: assistant.displayName ?? assistant.username,
            avatarUrl: assistant.avatarUrl ?? null,
            /* The person's words only; the agent-facing facts (which item,
               list then guide) reach the run another way. See
               `robutlerDrivePrefill` (F4, the round 8 dev run, 2026-09-28). */
            prefillText: robutlerDrivePrefill(t, { widgetName: meta?.displayName ?? 'this app', task: driveTask }),
          },
        },
      });
      // Picking an agent here IS the consent gesture, so it must write the
      // grant — mounting the agent alone gives it nothing. Append to the app's
      // allow-list (the same `state.agentControl` shape the app's settings
      // panel writes) rather than replacing it, so driving with a second agent
      // doesn't silently revoke the first. Keyed by agent DB id: that is what
      // the bus precheck matches, and a @handle would silently no-op.
      //
      // ORDER MATTERS: this runs only AFTER the mount resolves. `addItem` can
      // reject (e.g. "Agent not accessible"), and granting first would leave a
      // standing grant for an agent that never arrived — a permission the user
      // never sees on a canvas and cannot revoke from the chip list.
      const current = item?.state?.agentControl;
      const currentAllow = Array.isArray(current) ? current : current?.allow ?? [];
      if (
        !currentAllow.includes(assistant.id) &&
        !currentAllow.includes('everyone') &&
        !currentAllow.includes('*')
      ) {
        patchItemState(itemId, {
          agentControl: { allow: [...currentAllow, assistant.id] },
        });
      }
      return true;
    } catch (err) {
      console.warn('[content-node] assistant spawn for drive failed', err);
      return false;
    }
  }, [addItem, itemId, meta?.displayName, item?.state?.agentControl, patchItemState, t, driveTask]);

  // ── Publish lifecycle (owned remixable widget) ────────────────────
  // For a widget the current user OWNS, the header pill becomes a
  // Publish → Published → Update lifecycle instead of Remix.
  const { user } = useAuth();
  // Server-resolved ownership (creator OR owner of the authoring agent) with a
  // direct-creator fallback for cached/older meta that predates `ownedByMe`.
  const isOwnedByMe = meta?.ownedByMe ?? (!!meta?.creatorId && !!user?.id && meta.creatorId === user.id);
  // Versioned publish only supports folder bundles (the editable remix-loop
  // widgets). Owned external-cooperative widgets fall back to the Remix pill.
  // NEVER for an app-mounted DOCUMENT window (openWithWidgetMeta): its refId
  // is the FILE, and offering Publish there would publish the document as an
  // app — the field bug ("why do we see Publish?"). Publishing the app itself
  // lives on the APP's own window.
  const canPublishLifecycle =
    isOwnedByMe &&
    bundle?.kind === 'folder' &&
    !meta?.openWithWidgetMeta &&
    // System-seeded catalog apps ARE the catalog — no first-time
    // Publish on them, even for the seeding account (field: "why do we
    // see Publish for the published app?").
    !meta?.system;

  // First publish (or re-list an unlisted one): open the dedicated Publish app
  // dialog (name, description, marketplace category, list toggle, live preview).
  const doPublish = useCallback(() => {
    if (!refId) return;
    openPublishAppDialog({
      contentId: refId,
      name: meta?.displayName ?? cache.displayName ?? 'My app',
      category: typeof meta?.metadata?.surface === 'string'
        ? appCategoryFor(typeof meta?.metadata?.widgetType === 'string' ? meta.metadata.widgetType : '', meta.metadata.surface as string)
        : undefined,
    });
  }, [refId, meta?.displayName, meta?.metadata, cache.displayName]);

  // Refresh `/info` so the publish pill flips (Publish/Update → Published) after
  // a publish completes — whether triggered here or from the publish dialog.
  const refreshPublishState = useCallback(async () => {
    if (!refId) return;
    const info = await fetch(`/api/content/${refId}/info`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    if (info) {
      setMeta((m) =>
        m
          ? {
              ...m,
              isDirty: info.isDirty ?? false,
              publishedVersionId: info.publishedVersionId ?? m.publishedVersionId,
              publishedPostId: info.publishedPostId ?? m.publishedPostId,
            }
          : m,
      );
    }
  }, [refId]);

  // The publish dialog dispatches this once a (re)publish lands; pick it up so
  // an owning canvas node's pill stays in sync without a reload.
  useEffect(() => {
    const onPublished = (e: Event) => {
      const id = (e as CustomEvent<{ contentId?: string }>).detail?.contentId;
      if (id && id === refId) void refreshPublishState();
    };
    window.addEventListener('workspace:widget-published', onPublished);
    return () => window.removeEventListener('workspace:widget-published', onPublished);
  }, [refId, refreshPublishState]);

  // Update: open the publish dialog in "update" mode so the creator can note
  // what's new and (optionally) tell the world with a prefilled announcement.
  const doUpdate = useCallback(() => {
    if (!refId) return;
    openPublishAppDialog({
      contentId: refId,
      name: meta?.displayName ?? cache.displayName ?? 'My app',
      description: typeof meta?.metadata?.description === 'string' ? meta.metadata.description : undefined,
      category: typeof meta?.metadata?.surface === 'string'
        ? appCategoryFor(typeof meta?.metadata?.widgetType === 'string' ? meta.metadata.widgetType : '', meta.metadata.surface as string)
        : undefined,
      alreadyPublished: true,
    });
  }, [refId, meta?.displayName, meta?.metadata, cache.displayName]);

  // Published (clean): open the post when listed; otherwise re-open the publish
  // dialog so an unlisted widget can be listed to a channel.
  const openPublished = useCallback(() => {
    if (meta?.publishedPostId) {
      window.open(`/p/${meta.publishedPostId}`, '_blank', 'noopener');
    } else {
      doPublish();
    }
  }, [meta?.publishedPostId, doPublish]);

  // The content row is a widget already when its contentType is
  // 'widget' (per the LEFT JOIN that informs `meta.contentType`).
  // We never want to "republish a widget as a widget", so hide the
  // action in that case.
  const isAlreadyWidget = meta?.contentType === 'widget';
  // Publish only makes sense for HTML documents. Non-HTML text files
  // (markdown, code) get the regular share + edit actions only.
  const showPublishAction = !!isHtmlContent && !isAlreadyWidget && !!refId;

  // Resolve the widget spec early — the actions useMemo below needs
  // to branch on `widgetSpec?.kind` to show the "Edit URL" toggle on
  // iframe-external widgets, and `widgetSpec` would TDZ if declared
  // after `actions` (`useMemo` reads it during the same render pass).
  // The render switch further down (the `isIframeWidget && …`
  // branches) shares this same value; one source of truth.
  // Resolve the render spec from the content row's own metadata; for a
  // project folder that has none, fall back to its open-with editor widget
  // (surfaced by `/info` as `openWithWidgetMeta`) so the folder mounts as an
  // inline app instance bound to itself.
  //
  // Version-pinned instances: when the user accepted an app update,
  // `state.appVersionContentId` names the SUCCESSOR version row — its
  // metadata supplies the spec (bundle folder, interface) while everything
  // identity-bearing (refId, rooms, grants, title) stays on `meta`. While the
  // override row is still loading the spec stays null (no old-code boot);
  // if the override row failed to load, fall back to the pinned version.
  const overrideSettled = !appVersionOverride || overrideMeta?.forId === appVersionOverride;
  const widgetSpec = meta
    ? !overrideSettled
      ? null
      : ((overrideSettled && appVersionOverride && overrideMeta?.metadata
          ? resolveWidget(overrideMeta.metadata)
          : null) ??
        resolveWidget(meta.metadata ?? null) ??
        resolveWidget(meta.openWithWidgetMeta ?? null))
    : null;
  const isIframeWidget =
    (widgetSpec?.kind === 'iframe' || widgetSpec?.kind === 'iframe-external') &&
    !!widgetSpec.entry;
  const isExternalIframe = widgetSpec?.kind === 'iframe-external';

  // Drive gate: the app declares an agent command interface (registry spec for
  // first-party widgets, bundle manifest for community remixes) — the Drive
  // pill hands an AI those controls over the live command bus.
  const manifestCommands = (bundle as { manifestSnapshot?: { _meta?: { ui?: { interface?: { commands?: Record<string, unknown> } } } } } | null)
    ?.manifestSnapshot?._meta?.ui?.interface?.commands;
  const isDrivable =
    isIframeWidget &&
    !!((widgetSpec?.interface?.commands && Object.keys(widgetSpec.interface.commands).length > 0) ||
      (manifestCommands && Object.keys(manifestCommands).length > 0));

  // The window's REAL, shareable content id — what BOTH "Open full screen"
  // and Share must address.
  //
  // SECURITY: the standalone `/w/<id>` fullscreen page resolves a COLLAB
  // widget's room as `<id>:MAIN`. That is only correct when `<id>` is the
  // widget's REAL content id — a bound project folder, or a folder/doc it was
  // opened from. A FRESH SCRATCH draft's `refId` is the shared widget CATALOG
  // row, and its live document is a PRIVATE per-instance room on the canvas;
  // `/w/<catalog>` would instead join the globally-shared `<catalog>:MAIN`
  // room, exposing ANOTHER user's in-progress document. So for collab widgets
  // we only allow the fullscreen NAVIGATION when a real content id exists, and
  // otherwise fall back to the in-canvas takeover (same bridge, same room).
  //
  // Share ran on `refId` instead and inherited none of that: an app instance's
  // refId IS the catalog row, so sharing a SAVED document minted a link to the
  // APP ("Docs"), not the document. Recipients opened a blank editor — and
  // logged-out ones got "Could not open: permission: Unauthorized", because a
  // `/w/<catalog>` mount joins a private per-mount SCRATCH room and that room
  // class is login-required. One id, both doors.
  const shareableContentId: string | null = widgetSpec?.collab
    ? ((item?.state?.projectFolderId as string | undefined) ??
        (cache.contentType && cache.contentType !== 'widget' ? refId : null))
    : refId;

  // Everything below runs BEFORE the `!item` early return: `maximizeApp`
  // and the fullscreen bookkeeping are hooks, and an app window that loses
  // its item for a render (the rename harness does this) must not change the
  // hook count. It also sits BEFORE the header actions memo: the overflow's
  // "Open full screen" is the same full screen as the green dot.
  // App nodes (iframe widgets) skip the in-canvas takeover: it portals the
  // frame to `document.body`, which REMOUNTS the iframe (one `load`, measured
  // 2026-09-21), so the app would lose its state. It used to navigate to
  // `/w/<id>?ws=&item=`, which always mounts a synthetic, ephemeral share
  // (the e2e run of 2026-09-26: Revent refused every create there), and then
  // to present the `.react-flow__node` wrapper with the browser's Fullscreen
  // API, whose refusal wrote a viewport-sized window into the shared
  // document. Other content (docs / images) keeps the in-canvas fullscreen
  // via `onMaximize`.
  //
  // DESKTOP FULL SCREEN (2026-09-29, lane D; the Stage algorithm, rules 12
  // to 17). The green dot shows the app full screen on THIS device, on a
  // temporary screen of its own right after the one it came from: the store
  // lists it (`desktopFullscreen`, local only), the canvas draws the SAME
  // node at a display-only size under lane B's app bar (the iframe never
  // moves: zero loads) and points the camera at it. It is the PAGE's own
  // full-screen view: the browser is never asked for full screen (the
  // owner, 2026-09-30; maximize-app-node.ts has the history), so Stage and
  // the other screens stay one chord away and the browser's tabs and bar
  // stay where the person left them. The window's stored place never
  // changes, so other people keep seeing it where it was and exit restores
  // it exactly: this path never calls `patchItemState` and never falls back
  // to `toggleMaximizeToViewport` (that stays the header double-click's
  // window-manager maximize, a deliberate shared edit).
  const presentsInPlace = isIframeWidget;
  // The handset test the canvas and NodeFrame share (`!isMd ||
  // handsetLandscape`). On a handset the window is the phone app view
  // (handoff 2026-09-29, section 5): "Full screen" there is NodeFrame's
  // in-tab flag, so the browser-fullscreen row below is not offered, and
  // desktop full screen can never start from a phone.
  const isMdViewport = useIsMd();
  const handsetLandscape = useHandsetLandscape();
  const handset = !isMdViewport || handsetLandscape;

  // `appBodyRef` is the box around the bridge (the iframe's parent).
  const appBodyRef = useRef<HTMLDivElement>(null);
  const desktopFullscreen = useDesktopFullscreen();
  const isDesktopFullscreen = desktopFullscreen.some((r) => r.id === itemId);
  const isDesktopFullscreenRef = useRef(isDesktopFullscreen);
  isDesktopFullscreenRef.current = isDesktopFullscreen;
  const maximizeApp = useCallback(() => {
    if (!presentsInPlace || handset) return;
    if (isDesktopFullscreenRef.current) {
      // The way back (rule 15): the canvas removes the temporary screen and
      // returns the view "framed as before".
      window.dispatchEvent(
        new CustomEvent('ws:screen', { detail: { kind: 'fullscreen-exit', itemId } }),
      );
      return;
    }
    window.dispatchEvent(
      new CustomEvent('ws:screen', { detail: { kind: 'fullscreen-enter', itemId } }),
    );
  }, [presentsInPlace, handset, itemId]);

  // Header is intentionally lean: a SINGLE inline toggle (preview ↔
  // source) plus a `⋯` overflow menu (NodeFrame renders it) holding
  // every secondary action. Save is NOT in the header — it lives inside
  // the source editor (SaveBadge), alongside Cmd-S + idle autosave.
  const { actions, overflowActions } = useMemo<{
    actions: NodeFrameAction[];
    overflowActions: NodeFrameAction[];
  }>(() => {
    if (!meta) return { actions: [], overflowActions: [] };

    // ── Primary: the inline dot strip. The preview/source `<>` toggle
    // used to live here; it now lives in the `⋯` overflow menu (built
    // below) so the header keeps just the universal window controls.
    const primary: NodeFrameAction[] = [];
    // The `<>` view toggle (preview ↔ source / URL / rendered). Collected
    // here, prepended into the overflow `⋯` menu once it's built. ONLY
    // for plain content windows: an app-mounted document
    // (openWithWidgetMeta) is edited by ITS APP — a raw-buffer "Edit
    // source" door on that window is developer noise (field: "wtf is
    // edit source"). Power users still reach the raw file via the node
    // menu's "Open in full editor".
    const viewToggle: NodeFrameAction[] = [];
    const appMounted = !!meta.openWithWidgetMeta;
    if (isHtmlContent && !appMounted) {
      const inSource = view === 'source';
      viewToggle.push({
        id: 'view-toggle',
        label: inSource ? t('workspace.canvas.node.preview') : t('workspace.canvas.node.edit'),
        tone: inSource ? 'blue' : 'amber',
        icon: inSource ? Eye : Code,
        onClick: () => patchItemState(itemId, { view: inSource ? 'preview' : 'source' }),
      });
    } else if (isMarkdownContent && !appMounted) {
      const inSource = view === 'source';
      viewToggle.push({
        id: 'view-toggle',
        label: inSource ? t('workspace.node.rendered') : t('workspace.canvas.node.edit'),
        tone: inSource ? 'blue' : 'amber',
        icon: inSource ? Eye : Code,
        onClick: () => patchItemState(itemId, { view: inSource ? 'preview' : 'source' }),
      });
    }

    // Shield nudge: the widget asked for a sensitive feature the user
    // hasn't granted. One inline amber dot → opens Settings to grant.
    if (pendingGrants.length > 0) {
      primary.push({
        id: 'permission-shield',
        label: `Grant ${pendingGrants.join(', ')}`,
        tone: 'amber',
        icon: ShieldAlert,
        onClick: () => patchItemState(itemId, { view: 'settings' }),
        active: view === 'settings',
      });
    }

    // ── Overflow: everything secondary, behind the ⋯ menu ──
    const overflow: NodeFrameAction[] = [];
    if (isTextLike && meta.contentType === 'function' && refId) {
      overflow.push({
        id: 'validate',
        label:
          validateStatus === 'validating'
            ? 'Validating…'
            : validateStatus === 'errors'
              ? `${errorCount} ${errorCount === 1 ? 'error' : 'errors'}`
              : validateStatus === 'ok'
                ? t('workspace.node.valid')
                : t('workspace.node.validate'),
        tone: validateStatus === 'errors' ? 'rose' : 'purple',
        icon: ShieldCheck,
        onClick: () => void doValidate(),
        active: validateStatus === 'validating',
      });
    }
    if (isTextLike && refId) {
      overflow.push({
        id: 'history',
        label: 'Version history',
        tone: 'slate',
        icon: HistoryIcon,
        onClick: () =>
          patchItemState(itemId, {
            view: view === 'history' ? (isHtmlContent ? 'source' : 'preview') : 'history',
          }),
        active: view === 'history',
      });
    }
    if (showPublishAction) {
      overflow.push({
        id: 'publish-widget',
        label:
          publishStatus === 'publishing'
            ? 'Publishing…'
            : publishStatus === 'published'
              ? 'Published'
              : publishStatus === 'error'
                ? t('workspace.node.failed')
                : 'Publish as widget',
        tone:
          publishStatus === 'error' ? 'rose' : publishStatus === 'published' ? 'green' : 'purple',
        icon: Blocks,
        onClick: () => void doPublishAsWidget(),
        active: publishStatus === 'publishing' || publishStatus === 'published',
      });
    }
    // Remix is surfaced as a prominent header pill (see `headerAccessory`
    // on NodeFrame below), not a dot in this overflow menu.
    if (isExternalCooperative) {
      overflow.push({
        id: 'backing-agent',
        label: 'Backed by remote',
        tone: 'slate',
        icon: Info,
        onClick: () => setBackingAgentOpen((v) => !v),
        active: backingAgentOpen,
      });
    }
    // Apps get "Open full screen" in the overflow too: the SAME in-place
    // maximize as the green dot (native fullscreen on the node, the viewport
    // maximize as its fallback). It used to open `/w/{id}` in a new tab,
    // which mounts a synthetic read-only share of the person's own window
    // (e2e run of 2026-09-26: Revent refused every create there). Previewing
    // what recipients see is the Share dialog's link, not this row.
    // Not on a handset: there the phone app bar's ⋯ offers the in-tab full
    // screen instead (see `handset` above), and this row must not exist.
    if (presentsInPlace && !handset) {
      overflow.push({
        id: 'open-fullscreen',
        label: t('workspace.node.openFullScreen'),
        tone: 'slate',
        icon: Maximize2,
        onClick: () => maximizeApp(),
      });
    }
    // (The green maximize dot presents the window fullscreen in place, so no
    // separate overflow action.)
    // Share addresses `shareableContentId`, never `refId` — see the note on
    // its declaration. A collab app whose document is still an unsaved draft
    // has NO shareable content (its room is private to this canvas instance),
    // so the row says what is missing instead of minting a link to the app
    // itself. The reason belongs in the LABEL, not in post-click feedback:
    // this menu closes on select, so anything said afterwards is never seen.
    // And the row is DISABLED, not a live row with a no-op onClick — an
    // enabled-looking item whose click just closed the menu read as Share
    // silently failing. (The save flow itself lives inside the app — its own
    // file menu's Save — so there is nothing the host could open from here.)
    const canShare = !!shareableContentId && !shareableContentId.startsWith('inline:');
    overflow.push({
      id: 'share',
      label: canShare ? t('workspace.canvas.node.share') : 'Save it first to share',
      tone: canShare ? 'slate' : 'amber',
      icon: Share2,
      disabled: !canShare,
      onClick: () => {
        if (canShare) setShareOpen(true);
      },
    });
    overflow.push({
      id: 'settings',
      label: 'Settings',
      tone: 'slate',
      icon: Settings,
      onClick: () =>
        patchItemState(itemId, { view: view === 'settings' ? 'preview' : 'settings' }),
      active: view === 'settings',
    });

    // Publish lifecycle lives HERE, in the frame's single `⋯`. It used to have its own `⋯` next to
    // [Remix][Drive], which put two identical dot-menus side by side in the header — the frame's and the
    // app pill's — with no way to tell which held what. One overflow per window.
    // `leavesFullScreen` (lane B): the publish dialog and the backmix dialog
    // are drawn outside this window, so from desktop full screen these rows
    // leave full screen first (see NodeFrameAction.leavesFullScreen).
    if (canPublishLifecycle) {
      if (!meta.publishedVersionId) {
        overflow.push({ id: 'publish', label: 'Publish', tone: 'purple', icon: Rocket, onClick: doPublish, leavesFullScreen: true });
      } else if (meta.isDirty) {
        overflow.push({ id: 'publish-update', label: t('workspace.node.publishUpdate'), tone: 'purple', icon: RefreshCw, onClick: doUpdate, leavesFullScreen: true });
      }
    }

    // Remix-only: offer to propose this fork's feature back to the ORIGINAL app
    // (backmix). `publishChannelSlug` is set by /info only when the widget has
    // remix lineage, so it doubles as the "is a remix" signal here.
    if (canPublishLifecycle && meta.publishChannelSlug) {
      overflow.push({
        id: 'propose-backmix',
        label: 'Propose to original',
        tone: 'purple',
        icon: GitMerge,
        onClick: () => setBackmixOpen(true),
        leavesFullScreen: true,
      });
    }

    // `<>` view toggle leads the overflow menu (when present).
    return { actions: primary, overflowActions: [...viewToggle, ...overflow] };
  }, [
    meta,
    view,
    itemId,
    patchItemState,
    isHtmlContent,
    isMarkdownContent,
    isTextLike,
    refId,
    validateStatus,
    errorCount,
    doValidate,
    showPublishAction,
    publishStatus,
    doPublishAsWidget,
    isExternalCooperative,
    backingAgentOpen,
    widgetSpec?.kind,
    externalEditOpen,
    pendingGrants,
    canPublishLifecycle,
    doPublish,
    doUpdate,
    shareableContentId,
    presentsInPlace,
    handset,
    maximizeApp,
    t,
  ]);


  if (!item) return null;

  // The window header names the APP — the FILE name already lives in the
  // widget's own collab header, so echoing it here printed the same name
  // stacked twice (the old code even preferred the widget-published doc title
  // for exactly that duplicate). An app mount resolves its editor's name from
  // the open-with bundle manifest (documents) or the widget row itself
  // (scratch instances, whose displayName IS the app name). Plain content
  // (files, folders, posts) keeps the row/doc name as before.
  const docTitle =
    typeof item.state.docTitle === 'string' && item.state.docTitle.trim()
      ? (item.state.docTitle as string)
      : null;
  const openWithSnap = (meta?.openWithWidgetMeta as {
    bundle?: { manifestSnapshot?: { name?: unknown; _meta?: { ui?: { title?: unknown } } } };
  } | null)?.bundle?.manifestSnapshot;
  const openWithUiTitle = openWithSnap?._meta?.ui?.title;
  // The seeded bundle manifest's top-level `name` is the widget's display
  // title (def.title); 'legacy-widget' is the synthetic backfill name.
  const openWithAppName =
    (typeof openWithUiTitle === 'string' && openWithUiTitle.trim() ? openWithUiTitle.trim() : null) ??
    (typeof openWithSnap?.name === 'string' && openWithSnap.name.trim() && openWithSnap.name !== 'legacy-widget'
      ? openWithSnap.name.trim()
      : null);
  const title = isIframeWidget
    ? ((meta?.openWithWidgetMeta ? openWithAppName : null) ?? meta?.displayName ?? cache.displayName ?? 'App')
    : (docTitle ?? meta?.displayName ?? cache.displayName ?? 'Content');
  /* The app row running in this window, by the same fallback as its type: the
     item's own row for an app, the open-with handler's for a folder or a
     document. The bridge and the Drive dialog both send it, because the
     server acts for an app only from its own catalog row (S-173). */
  const appContentId =
    typeof meta?.metadata?.widgetType === 'string'
      ? (refId && !refId.startsWith('inline:') ? refId : undefined)
      : typeof (meta?.openWithWidgetMeta as { widgetType?: unknown } | null)?.widgetType === 'string'
        ? (meta?.openWithWidgetId ?? undefined)
        : undefined;

  // Widget content is a polymorphic surface: the registry resolves
  // metadata.widgetType into one of {native, iframe, projection-only}.
  // Native widgets are handled by their own dedicated nodes (agent,
  // shell, terminal), so by the time we get here we're either looking
  // at an iframe widget or a projection-only widget (rendered by
  // DslView in a follow-up).
  //
  // `widgetSpec`, `isIframeWidget`, `isExternalIframe` are hoisted
  // above the `actions` useMemo (see comment above its declaration).
  // Projection-only widgets (weather, agent-status, …) render their UI
  // entirely from a declarative `metadata.projection` spec via DslView.
  // They have NO file bytes to download / preview, so we MUST branch to
  // DslView before the generic ContentPreviewBody fallback — otherwise
  // the file-card chrome (with its "Download" affordance) leaks onto a
  // widget that has nothing to download.
  const isProjectionWidget = widgetSpec?.kind === 'projection-only';
  // Same hazard as above for the brief window between `meta` arriving
  // from the workspace-wide cache (which doesn't carry the metadata
  // JSONB — see `WorkspaceContentCache` in `store.tsx`) and the
  // `/api/content/:id/info` round-trip filling in `metadata.metadata`:
  // `widgetSpec` resolves to `null` so the render below falls all the
  // way through to `ContentPreviewBody`, briefly painting the file-card
  // chrome (icon + "Download" affordance) over what is about to become
  // a Weather / Snake / Calculator tile. Detect the "widget waiting for
  // metadata" case and render a neutral loading panel instead.
  const isWidgetContentPending =
    (meta?.contentType === 'widget' && widgetSpec === null) ||
    // A project folder whose open-with editor hasn't resolved yet — show the
    // neutral loading panel instead of flashing the raw folder card.
    (meta?.contentType === 'folder' &&
      typeof (meta?.metadata as Record<string, unknown> | null)?.projectMime === 'string' &&
      widgetSpec === null);
  const inlineProjectionSpec: DslSpec | null = (() => {
    if (!isProjectionWidget) return null;
    const m = meta?.metadata as Record<string, unknown> | undefined;
    const proj = m?.projection as
      | { version?: number; root?: unknown; spec?: DslSpec }
      | undefined;
    // Two on-disk shapes are observed in the wild: the seed scripts
    // store the spec directly under `projection` (no `.spec`
    // wrapper), and the ProjectionMetadata type expects `.spec`. Try
    // both before giving up.
    if (proj?.spec && typeof proj.spec === 'object') return proj.spec;
    if (proj && typeof proj.root !== 'undefined' && proj.version === 1) {
      return proj as DslSpec;
    }
    return null;
  })();

  // The app's Update, one function for the title-bar pill and the desktop
  // full-screen ⋯ row (lane B), so the two can never do different things.
  // Version-pinned instance: adopt the successor version row (code only;
  // refId, rooms and grants stay). Catalog-row instance: the remount alone
  // picks up the refreshed bundle.
  const applyAppUpdate = () => {
    if (versionUpdateAvailable && successorId) {
      patchItemState(itemId, { appVersionContentId: successorId });
    }
    setAppUpdateNonce((n) => n + 1);
    markAppFresh();
  };

  return (
    <>
    <NodeFrame
      itemId={itemId}
      title={title}
      // The title-bar ad slot's context (ads build plan section 2.4): the app
      // running in this window, only once it has resolved to an iframe app.
      // A pending, missing or plain-file window passes nothing, so error and
      // empty screens never carry an ad.
      adAppId={isIframeWidget && appContentId ? appContentId : null}
      actions={actions}
      overflowActions={overflowActions}
      // The pills' verbs as rows for the phone app bar's ⋯ sheet (Remix and
      // Drive first, as they were first on the bar; wireframes v3 A). The
      // same dialogs the pills open; the pills themselves stay desktop-only.
      // The desktop full-screen app bar folds them into its ⋯ too (lane B);
      // both dialogs are drawn outside the window, so from the browser's
      // full screen they leave it first (`leavesFullScreen`).
      accessoryActions={
        meta && (isRemixable || isDrivable)
          ? [
              ...(isRemixable
                ? [
                    {
                      id: 'remix',
                      label: t('widget.remix'),
                      tone: 'purple' as const,
                      icon: GitFork,
                      disabled: remixLoading,
                      onClick: () => setRemixDialogOpen(true),
                      leavesFullScreen: true,
                    },
                  ]
                : []),
              ...(isDrivable
                ? [
                    {
                      id: 'drive',
                      label: t('workspace.node.drive'),
                      tone: 'purple' as const,
                      icon: Bot,
                      onClick: () => setDriveDialogOpen(true),
                      leavesFullScreen: true,
                    },
                  ]
                : []),
            ]
          : undefined
      }
      // The Update pill as a row for the desktop full-screen app bar (lane
      // B: every title-bar action stays reachable there). The phone sheet
      // does not read this prop.
      fullScreenActions={
        meta && (appUpdateAvailable || versionUpdateAvailable)
          ? [
              {
                id: 'update',
                label: t('workspace.node.updateApp'),
                tone: 'blue' as const,
                icon: RefreshCw,
                onClick: applyAppUpdate,
              },
            ]
          : undefined
      }
      // Prominent gradient pill (mirrors "Open discovery"). Owners of a
      // remixable widget get a Publish → Published → Update lifecycle;
      // everyone else gets Remix. Icon-only on narrow widgets so it never
      // crowds the title. Rendered only after `meta` loads (no flash).
      headerAccessory={
        meta && (appUpdateAvailable || versionUpdateAvailable || isRemixable || isDrivable) ? (
          <>
            {appUpdateAvailable || versionUpdateAvailable ? (
              <button
                type="button"
                onClick={applyAppUpdate}
                title={t('workspace.node.updateAvailable')}
                className="mr-1 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-background/80 px-3 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-accent"
                data-testid="widget-update-cta"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {(item ? item.state.width : 0) >= 300 ? 'Update' : null}
              </button>
            ) : null}
            {meta && (isRemixable || isDrivable)
          ? (() => {
              // Responsive CTA pair — the two flagship verbs stay VISIBLE:
              //   wide (≥480px, incl. maximized/fullscreen): [Remix] [Drive] [⋯]
              //   mid  (300–480px): a segmented pill [Remix|Drive|▾]
              //   narrow (<300px): the single icon-only AI pill (space-starved)
              // The ⋯/▾ menu keeps the Publish lifecycle (and, on narrow, the
              // Drive/Remix rows themselves).
              const w = item ? item.state.width : 0;
              const openMenu = () => {
                const r = aiPillRef.current?.getBoundingClientRect();
                setAiMenuAt((cur) =>
                  cur ? null : r ? { top: r.bottom + 6, right: window.innerWidth - r.right } : null,
                );
              };
              if (w < 300) {
                return (
                  <span ref={aiPillRef} className="mr-1 flex items-center">
                    <GradientPillButton
                      icon={Bot}
                      label="AI"
                      loading={remixLoading}
                      iconOnly
                      onClick={openMenu}
                      data-testid="widget-ai"
                    />
                  </span>
                );
              }
              if (w >= 480) {
                return (
                  <span ref={aiPillRef} className="mr-1 flex items-center gap-1.5">
                    {isRemixable ? (
                      <button
                        type="button"
                        onClick={() => setRemixDialogOpen(true)}
                        disabled={remixLoading}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-background/80 px-3 py-1 text-[11px] font-semibold transition-colors hover:bg-accent disabled:opacity-60"
                        data-testid="widget-remix-cta"
                      >
                        <GitFork className="h-3.5 w-3.5" />
                        Remix
                      </button>
                    ) : null}
                    {isDrivable ? (
                      <GradientPillButton
                        icon={Bot}
                        label={t('workspace.node.drive')}
                        loading={remixLoading}
                        onClick={() => setDriveDialogOpen(true)}
                        data-testid="widget-drive-cta"
                      />
                    ) : null}
                    {/* No `⋯` here: at this width the publish lifecycle is in the FRAME's overflow menu
                        (see overflowActions). A second dot-menu beside the frame's own was pure ambiguity. */}
                  </span>
                );
              }
              return (
                <span ref={aiPillRef} className="mr-1 flex items-center">
                  <span className="inline-flex shrink-0 items-stretch overflow-hidden rounded-full border border-violet-300/70 bg-violet-50 text-violet-700 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-300">
                    {isRemixable ? (
                      <button
                        type="button"
                        onClick={() => setRemixDialogOpen(true)}
                        disabled={remixLoading}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition-colors hover:bg-violet-100 disabled:opacity-60 dark:hover:bg-violet-500/20"
                        data-testid="widget-remix-cta"
                      >
                        <GitFork className="h-3 w-3" />
                        Remix
                      </button>
                    ) : null}
                    {isRemixable && isDrivable ? <span aria-hidden className="w-px bg-violet-300/70 dark:bg-violet-500/40" /> : null}
                    {isDrivable ? (
                      <button
                        type="button"
                        onClick={() => setDriveDialogOpen(true)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition-colors hover:bg-violet-100 dark:hover:bg-violet-500/20"
                        data-testid="widget-drive-cta"
                      >
                        <Bot className="h-3 w-3" />
                        {t('workspace.node.drive')}
                      </button>
                    ) : null}
                    {canPublishLifecycle ? (
                      <>
                        <span aria-hidden className="w-px bg-violet-300/70 dark:bg-violet-500/40" />
                        <button
                          type="button"
                          onClick={openMenu}
                          aria-label={t('workspace.node.moreAiActions')}
                          className="inline-flex items-center px-1.5 py-1 transition-colors hover:bg-violet-100 dark:hover:bg-violet-500/20"
                          data-testid="widget-ai-more"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </>
                    ) : null}
                  </span>
                </span>
              );
            })()
          : null}
          </>
        ) : undefined
      }
      // Maximize. For an app (iframe widget) the green dot shows it full
      // screen on this device (`onMaximizeOverride`, see `maximizeApp`), and
      // while it is, NodeFrame's header is lane B's desktop app bar; for
      // other content it keeps the in-canvas takeover (`onMaximize`,
      // NodeFrame portals it).
      onMaximize={meta && !presentsInPlace ? () => {} : undefined}
      onMaximizeOverride={presentsInPlace ? maximizeApp : undefined}
      maximizeOverrideActive={isDesktopFullscreen && !handset}
      // Header double-click maximizes the app to the visible viewport
      // (and restores on the next double-click). Replaces the old
      // camera zoom-to-fit — the canvas is a fixed-zoom surface now,
      // so the WINDOW resizes instead of the camera moving.
      onFocus={meta ? toggleMaximizeToViewport : undefined}
      bodyPad={false}
      // Headerless content nodes (pasted images / videos) drop
      // the chrome row, so the floor isn't "the title + close
      // dots need to fit" — it's just "the picture stays legible
      // as a thumbnail". 48 px ≈ 2 snap units on the 24 px
      // lattice, matching the floor we use for sticky-note
      // shrink-to-icon UX. Chrome-on content nodes keep the
      // legacy 220×180 minimum so the title row and action
      // strip don't get clipped.
      // A widget's DECLARED minSize/maxSize (registry or manifest) is the
      // resize floor/ceiling; otherwise fall back to the type defaults. The
      // headerless (collapsed puck) mode keeps its 48px affordance.
      minWidth={item.state.hideHeader ? 48 : (widgetSpec?.minSize?.width ?? 220)}
      minHeight={item.state.hideHeader ? 48 : (widgetSpec?.minSize?.height ?? 180)}
      // Interactive widgets (docs editor, board, vector/video editor …) are
      // apps — let the user grow them as large as they like. Only STATIC content
      // (images / video / text) keeps the 1200 tile cap so it doesn't dominate
      // the canvas.
      maxWidth={widgetSpec?.maxSize?.width ?? (isIframeWidget ? 100000 : 1200)}
      maxHeight={widgetSpec?.maxSize?.height ?? (isIframeWidget ? 100000 : 1200)}
    >
      {remixNotice && (
        <div
          role="alert"
          className="flex items-start gap-2 px-3 py-1.5 text-[11px] leading-snug bg-rose-500/10 text-rose-700 dark:text-rose-300"
        >
          <span className="min-w-0 flex-1">{remixNotice}</span>
          <button
            type="button"
            onClick={() => setRemixNotice(null)}
            aria-label={t('workspace.canvas.node.close')}
            className="shrink-0 rounded p-0.5 hover:bg-rose-500/15"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      {!meta ? (
        // A window that is still finding out what it holds: one quiet line in
        // the middle, with the spinner the rest of the workspace uses. It was
        // twelve-pixel text in the top left corner of an otherwise empty
        // window, in English whatever the language (the Revent run on dev,
        // 2026-10-02: "Loading app..." in the corner under the update pill).
        <div
          className="flex h-full w-full items-center justify-center gap-2 p-4 text-sm text-muted-foreground"
          role="status"
          data-testid="window-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>{t('discover.loading')}</span>
        </div>
      ) : meta.unavailable ? (
        // Refused by the server: say so before any branch that would draw an
        // editor, an app or a file card over content this viewer cannot read.
        <ContentPreviewBody meta={meta} onOpenLightbox={() => {}} />
      ) : isWidgetContentPending ? (
        // `meta` is populated from the optimistic cache but
        // `metadata.metadata` (where `widgetType` / `iframeEntry` /
        // `projection` live) hasn't landed yet — render a soft
        // loading panel so the user never sees the misleading file
        // card with a Download button. Centred like the one above.
        <div
          className="flex h-full w-full items-center justify-center gap-2 p-4 text-sm text-muted-foreground"
          role="status"
          data-testid="window-loading-app"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>{t('workspace.node.loadingApp')}</span>
        </div>
      ) : isIframeWidget && widgetSpec?.entry && isExternalIframe && view === 'source' && refId ? (
        // External iframe-embed widgets get an inline URL editor
        // behind the "Edit URL" toggle. Lets users fix typos / tweak
        // query params (e.g. `?autoplay=1` on YouTube) / swap the
        // embed target without delete + re-paste. Save writes to
        // `metadata.bundle.entryUrl`; on success we flip back to
        // preview and the bridge re-mounts with the new src.
        //
        // First-party `iframe` widgets (kind: 'iframe', not
        // 'iframe-external') don't get this surface — their source
        // is the bundle, edited elsewhere.
        <ExternalSourceEditor
          contentId={refId}
          currentEntryUrl={widgetSpec.entry}
          onSaved={(newUrl) => {
            // Patch the local meta cache so the bridge picks up the
            // new src on the next render without a full /info
            // round-trip. The `bundle` shape mirrors what the server
            // writes; the spec resolver reads `bundle.entryUrl`.
            setMeta((m) => {
              if (!m) return m;
              const existing =
                (m.metadata && typeof m.metadata === 'object'
                  ? (m.metadata as Record<string, unknown>)
                  : {}) ?? {};
              const existingBundle =
                (existing.bundle && typeof existing.bundle === 'object'
                  ? (existing.bundle as Record<string, unknown>)
                  : {}) ?? {};
              return {
                ...m,
                metadata: {
                  ...existing,
                  bundle: { ...existingBundle, entryUrl: newUrl },
                },
              };
            });
            setExternalEditOpen(false);
          }}
        />
      ) : isIframeWidget && widgetSpec?.entry ? (
        // Iframe-kind widget: the bridge owns the sandboxed iframe,
        // postMessage terminator, and workspace.bus subscription. The
        // source / history toggles don't apply to widgets (those edit
        // content bytes, not widget UI), so the bridge renders for the
        // body. EXCEPT `settings`, which carries the per-widget permission
        // grants (showPermissions is iframe-gated): it opens as a centered
        // dialog (WidgetSettingsModal) that portals above the canvas, so the
        // bridge stays mounted underneath and a feature grant re-keys it in
        // place without tearing down live widget state.
        <div ref={appBodyRef} className="relative h-full w-full">
          <WidgetBridge
            // Re-key on Update: a fresh bridge rebuilds the iframe against the
            // no-cache bundle routes, so the widget reboots on the reseeded
            // code (documents live in the collab room and survive the reboot).
            key={`wb-${appUpdateNonce}`}
            src={widgetSpec.entry}
            workspaceId={workspace.id}
            itemId={itemId}
            title={meta.displayName}
            external={isExternalIframe}
            widgetSpec={widgetSpec}
            // A PROJECT FOLDER carries no `widgetType` of its own — the type
            // lives on the open-with handler widget, the same fallback that
            // resolved `widgetSpec` above. Anything keyed by widget type
            // (per-type bridge policy, telemetry) silently loses the folder
            // mount without it — the ONLY surface where a project app
            // reaches its store (found on prod connecting Reach, 2026-08-12).
            widgetType={
              typeof meta.metadata?.widgetType === 'string'
                ? meta.metadata.widgetType
                : typeof (meta.openWithWidgetMeta as { widgetType?: unknown } | null)?.widgetType === 'string'
                  ? ((meta.openWithWidgetMeta as { widgetType: string }).widgetType)
                  : undefined
            }
            appContentId={appContentId}
            onDriveWith={(task) => { setDriveTask(task); setDriveDialogOpen(true); }}
          />
          {/* Boot overlay: app name + spinner while the bridge handshake
              hasn't reached 'ready' (read from the widget log buffer; E3). */}
          <WidgetBootOverlay itemId={itemId} appName={title} />
          {view === 'settings' ? (
            <WidgetSettingsModal
              meta={meta}
              onMetaChange={(patch) =>
                setMeta((m) => (m ? { ...m, ...patch } : m))
              }
              itemId={itemId}
              showPermissions
              onClose={() => patchItemState(itemId, { view: 'preview' })}
            />
          ) : null}
        </div>
      ) : isProjectionWidget ? (
        // Projection-only widget (no iframe, no file bytes). Render
        // the inline DSL spec right away so the user sees the tile
        // chrome on first paint; DslView's `liveSource` then re-fetches
        // via `/api/items/:itemId/projection` whenever the bus
        // signals a relevant `kv` write. KV bootstraps empty — the
        // first projection fetch backfills it.
        inlineProjectionSpec ? (
          <div className="h-full w-full p-3 overflow-hidden">
            <DslView
              spec={inlineProjectionSpec}
              kv={EMPTY_KV}
              liveSource={{ workspaceId: workspace.id, itemId }}
            />
          </div>
        ) : (
          <div className="h-full w-full p-4 text-xs text-muted-foreground">
            {t('workspace.node.projectionUnavailable')}
          </div>
        )
      ) : view === 'settings' ? (
        <WidgetSettingsModal
          meta={meta}
          onMetaChange={(patch) => setMeta((m) => (m ? { ...m, ...patch } : m))}
          itemId={itemId}
          showPermissions={isIframeWidget || meta.contentType === 'widget'}
          onClose={() => patchItemState(itemId, { view: 'preview' })}
        />
      ) : view === 'history' && refId ? (
        // Version history view. Replaces the editor body while open;
        // restoring a version reloads the text so the editor reflects
        // the new bytes immediately. The aside's intrinsic w-80 is
        // overridden so it fills the widget body regardless of size.
        <div className="ws-versions-host nowheel ws-selectable h-full w-full flex">
          <VersionsPanel
            contentId={refId}
            onClose={() =>
              patchItemState(itemId, {
                view: isHtmlContent ? 'source' : 'preview',
              })
            }
            onRestored={reloadText}
          />
        </div>
      ) : isText(meta.mimeType) && !isHtmlContent ? (
        // Plain text / code / JSON / markdown: CodeMirror is the
        // canonical view. For markdown we flip the editor's `richMode`
        // extension on for `view === 'preview'` (the default) so the
        // first paint is a rendered markdown surface — matches the
        // /content/[id] page. The user can still edit; toggling
        // Edit source disables `richMode` and shows the raw buffer.
        // The wheel must stay with the editor for vertical scrolling,
        // so opt out of the React Flow zoom-on-scroll with `nowheel`.
        <TextEditorBody
          draft={draftText}
          language={languageForMime(meta.mimeType, meta.displayName)}
          onChange={setDraftText}
          onSaveRequest={() => void doSave()}
          onViewReady={(v) => {
            editorViewRef.current = v;
          }}
          status={saveStatus}
          dirty={dirty}
          isFunction={meta.contentType === 'function'}
          richMode={isMarkdownContent && view !== 'source'}
        />
      ) : isHtmlContent && view === 'source' ? (
        <TextEditorBody
          draft={draftText}
          language={languageForMime(meta.mimeType, meta.displayName)}
          onChange={setDraftText}
          onSaveRequest={() => void doSave()}
          onViewReady={(v) => {
            editorViewRef.current = v;
          }}
          status={saveStatus}
          dirty={dirty}
          isFunction={false}
        />
      ) : (
        <ContentPreviewBody
          meta={meta}
          htmlSource={isHtmlContent ? draftText : null}
          onOpenLightbox={() => setLightbox(true)}
          // Headerless mode (e.g. pasted images / videos) promotes
          // the body to a drag-handle — see node-frame.tsx. Click
          // semantics there would conflict with the drag gesture
          // (and the user can't "click into" a chromeless picture
          // anyway), so we suppress the lightbox-on-click for
          // these nodes. Right-click → "Open in full editor"
          // remains the path to the full-screen view.
          disableLightboxOnClick={!!item.state.hideHeader}
          // Natural-size sync for headerless image / video
          // bodies. Two jobs:
          //
          //   (a) Record the source aspect ratio on
          //       `state.cache.aspectRatio` so NodeFrame's resize
          //       gesture can lock to it.
          //   (b) Reflow the node bounds to match the picture's
          //       intrinsic aspect (preserving area) whenever the
          //       current bounds drift from the source ratio. This
          //       fires on every paint — new pastes get the right
          //       shape immediately, AND old nodes (created before
          //       the aspect-lock feature shipped) self-heal on
          //       their next load so connection handles end up
          //       flush against the picture's edges instead of
          //       hovering on `object-contain` empty space.
          //
          // Skipped when the chrome header is on — the user
          // explicitly asked for a framed widget and we don't
          // want to fight their resize gestures.
          onNaturalSize={
            item.state.hideHeader
              ? (w, h) => {
                  if (!w || !h) return;
                  const ratio = w / h;
                  const cache = item.state.cache ?? {};
                  const currentRatio = item.state.width / item.state.height;
                  const drift = Math.abs(currentRatio - ratio) / ratio;
                  // Tolerance: < 1 % aspect drift counts as
                  // "already correct". We round to ints before
                  // PATCHing so a sub-pixel rounding error never
                  // re-fires this branch on the next render.
                  const cachedRatio =
                    typeof cache.aspectRatio === 'number'
                      ? cache.aspectRatio
                      : null;
                  const cacheDrift =
                    cachedRatio == null
                      ? Infinity
                      : Math.abs(cachedRatio - ratio) / ratio;
                  if (drift < 0.01 && cacheDrift < 0.01) return;
                  const area = item.state.width * item.state.height;
                  const newWidth = Math.round(Math.sqrt(area * ratio));
                  const newHeight = Math.round(newWidth / ratio);
                  patchItemState(itemId, {
                    width: newWidth,
                    height: newHeight,
                    cache: { ...cache, aspectRatio: ratio },
                  }, { background: true });
                }
              : undefined
          }
          // HTML widgets report their natural content size once on
          // load; fit the tile to it (clamped) a single time so e.g.
          // a 400×400 game board isn't clipped. `cache.autoSized`
          // makes it one-shot — a later manual resize is never
          // overridden.
          onContentSize={
            isHtmlContent
              ? (w, h) => {
                  const cache = (item.state.cache ?? {}) as Record<string, unknown>;
                  if (cache.autoSized) return;
                  const headerH = item.state.hideHeader ? 0 : 40;
                  const { width, height } = clampAutoFitSize(w, h, headerH);
                  patchItemState(itemId, {
                    width,
                    height,
                    cache: { ...cache, autoSized: true },
                  }, { background: true });
                }
              : undefined
          }
          // Buffer forwarded console/error lines per item so the
          // `workspace_widgets_logs` agent tool can read them.
          onLog={(entry) => appendWidgetLog(itemId, entry)}
          // Record readiness; on failure (blank / scripts blocked)
          // synthesize an error line so an agent sees it didn't load.
          onLoadState={(state) => {
            setWidgetLoadState(itemId, state);
            if (state === 'failed') {
              appendWidgetLog(itemId, {
                level: 'error',
                message:
                  'widget failed to load — blank document or scripts blocked (CSP). No content rendered.',
                ts: Date.now(),
              });
            }
          }}
        />
      )}

      {lightbox && meta && meta.url && (
        <Lightbox
          items={[
            {
              url: meta.url,
              // Widgets and synthetic types fall back to the generic
              // 'document' lightbox treatment so the type stays in the
              // narrow set `LightboxItem` expects.
              type:
                meta.contentType === 'folder' ||
                meta.contentType === 'function' ||
                meta.contentType === 'widget'
                  ? 'document'
                  : meta.contentType,
              filename: meta.displayName,
              mimeType: meta.mimeType,
              contentId: meta.id,
            },
          ]}
          onClose={() => setLightbox(false)}
        />
      )}
      {backingAgentOpen && isExternalCooperative && bundle?.kind === 'external-cooperative' && (
        <div className="absolute bottom-2 right-2 z-30 w-72 rounded-xl border border-border/40 bg-card p-3 text-xs shadow-lg">
          <div className="flex items-center gap-2 mb-1.5">
            <Info className="h-3.5 w-3.5 text-primary" />
            <span className="font-medium">
              {t('widget.backingAgent.label')}
            </span>
          </div>
          <p className="text-muted-foreground leading-snug mb-2">
            This widget is hosted by an external MCP App. Tool calls and
            manifest changes flow through{' '}
            <span className="font-mono break-all">
              {new URL(bundle.manifestUrl).hostname}
            </span>
            .
          </p>
          <Link
            href="/docs/widgets/authoring"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {t('workspace.node.learnRemoteManifest')} →
          </Link>
        </div>
      )}
      {shareOpen && meta && shareableContentId && (
        <ShareDialog
          kind="content"
          contentIds={[shareableContentId]}
          onClose={() => setShareOpen(false)}
          onSaved={() => {
            // Re-fetch info so the visibility badge + grant count refresh
            // immediately. The same /info round-trip the mount effect
            // performed runs again here.
            fetch(`/api/content/${shareableContentId}/info`)
              .then((r) => (r.ok ? r.json() : null))
              .then((data) => {
                if (!data) return;
                setMeta((m) =>
                  m
                    ? {
                        ...m,
                        linkVisibility: data.linkVisibility ?? m.linkVisibility,
                        effectiveVisibility:
                          data.effectiveVisibility ?? m.effectiveVisibility,
                        visibilitySource: data.visibilitySource ?? m.visibilitySource,
                        visibilitySourceName:
                          data.visibilitySourceName ?? m.visibilitySourceName,
                        grantCount: data.grantCount ?? m.grantCount,
                        shareToken: data.shareToken ?? m.shareToken,
                      }
                    : m,
                );
              })
              .catch(() => {});
          }}
        />
      )}
    </NodeFrame>
    <RemixWithDialog
      open={remixDialogOpen}
      onClose={() => setRemixDialogOpen(false)}
      widgetName={openWithAppName ?? meta?.displayName ?? 'widget'}
      onRobutler={() => { void doRemix(); }}
      createPrivateCopy={createPrivateCopy}
    />
    {aiMenuAt && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[200]"
            onClick={() => setAiMenuAt(null)}
            onKeyDown={(e) => { if (e.key === 'Escape') setAiMenuAt(null); }}
          >
            <div
              className="absolute min-w-[200px] rounded-xl border border-border bg-background p-1 shadow-2xl"
              style={{ top: aiMenuAt.top, right: aiMenuAt.right }}
              onClick={(e) => e.stopPropagation()}
              role="menu"
              aria-label={t('workspace.node.aiActions')}
            >
              {/* On split-CTA widths the header buttons already carry these two */}
              {isDrivable && !splitCta ? (
                <button
                  role="menuitem"
                  onClick={() => { setAiMenuAt(null); setDriveDialogOpen(true); }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium hover:bg-accent"
                  data-testid="widget-drive"
                >
                  <Bot className="h-4 w-4 text-muted-foreground" />
                  {t('workspace.node.driveThisApp')}
                </button>
              ) : null}
              {isRemixable && !splitCta ? (
                <button
                  role="menuitem"
                  onClick={() => { setAiMenuAt(null); setRemixDialogOpen(true); }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium hover:bg-accent"
                  data-testid="widget-remix"
                >
                  <GitFork className="h-4 w-4 text-muted-foreground" />
                  Remix
                </button>
              ) : null}
              {canPublishLifecycle ? (
                !meta?.publishedVersionId ? (
                  <button
                    role="menuitem"
                    onClick={() => { setAiMenuAt(null); doPublish(); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium hover:bg-accent"
                    data-testid="widget-publish"
                  >
                    <Rocket className="h-4 w-4 text-muted-foreground" />
                    Publish
                  </button>
                ) : meta?.isDirty ? (
                  <button
                    role="menuitem"
                    onClick={() => { setAiMenuAt(null); doUpdate(); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium hover:bg-accent"
                    data-testid="widget-update"
                  >
                    <RefreshCw className="h-4 w-4 text-muted-foreground" />
                    {t('workspace.node.publishUpdate')}
                  </button>
                ) : (
                  <button
                    role="menuitem"
                    onClick={() => { setAiMenuAt(null); openPublished(); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium hover:bg-accent"
                    data-testid="widget-published"
                  >
                    <Check className="h-4 w-4 text-emerald-500" />
                    Published
                  </button>
                )
              ) : null}
            </div>
          </div>,
          document.body,
        )
      : null}
    <DriveWithDialog
      open={driveDialogOpen}
      onClose={() => setDriveDialogOpen(false)}
      target={{
        widgetName: openWithAppName ?? meta?.displayName ?? cache.displayName ?? 'this app',
        /* What the person's own agent for THIS app is minted from
           (`@you.revent`). A project folder carries no type of its own, and
           the dialog falls back to the shared assistant when it is absent. */
        widgetType: typeof meta?.metadata?.widgetType === 'string' ? meta.metadata.widgetType : undefined,
        appContentId,
        workspaceId: workspace.id,
        itemId,
        task: driveTask,
      }}
      onRobutler={(agent) => doDriveWithRobutler(agent)}
    />
    <BackmixDialog
      open={backmixOpen}
      onClose={() => setBackmixOpen(false)}
      mode="propose"
      appName={meta?.displayName ?? 'this app'}
      onPropose={async ({ message, markerIds }) => {
        if (!refId) return;
        try {
          await fetch(`/api/content/${refId}/backmix`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message, markerIds }),
          });
        } catch (err) {
          console.warn('[content-node] backmix propose failed', err);
        }
      }}
    />
    </>
  );
}

/**
 * Memoized export. Only re-renders when id / selection / dragging
 * change at the React Flow boundary — content state changes flow
 * through `useWorkspaceItem(id)` internally and are scoped to this
 * node's subscription.
 */
export const ContentNode = memo(ContentNodeImpl, (a, b) => {
  return (
    a.id === b.id &&
    a.selected === b.selected &&
    a.dragging === b.dragging
  );
});

/**
 * Editor body for text / code / markdown / source-view HTML.
 *
 * Wraps `CodeEditor` in a `nowheel` container so the editor's own
 * scroll wins over the React Flow canvas zoom (otherwise scrolling
 * inside the editor would zoom the workspace instead of the doc).
 *
 * Renders a tiny floating save-status pill in the top-right of the
 * editor surface. Status comes from the parent which owns the
 * debounced save lifecycle; we just paint whichever state is active.
 */
function TextEditorBody({
  draft,
  language,
  onChange,
  onSaveRequest,
  onViewReady,
  status,
  dirty,
  isFunction,
  richMode,
}: {
  draft: string | null;
  language: string;
  onChange: (next: string) => void;
  onSaveRequest: () => void;
  /** Forwarded to CodeEditor; the host caches the EditorView so it
   *  can paint manifest diagnostics into the gutter when the user
   *  hits Validate. */
  onViewReady?: (view: EditorView) => void;
  status: 'idle' | 'saving' | 'saved' | 'error';
  dirty: boolean;
  /**
   * When the content is a Robutler function (contentType === 'function'),
   * render the ManifestHelper above the editor so users can edit the
   * runtime/entrypoint frontmatter inline. Otherwise render plain editor.
   */
  isFunction: boolean;
  /**
   * Forwarded to `CodeEditor`. Only honored for `language === 'markdown'`
   * (the editor's `richMode` extension is a no-op for other languages).
   * Toggled by the markdown rendered/source frame action so the same
   * widget can flip between WYSIWYG and raw source without remount.
   */
  richMode?: boolean;
}) {
  if (draft == null) {
    return (
      <div className="h-full w-full p-4 text-xs text-muted-foreground">
        Loading…
      </div>
    );
  }
  return (
    <div className="nowheel relative h-full w-full flex flex-col">
      {isFunction && (
        <ManifestHelper source={draft} onPatchSource={onChange} />
      )}
      <div className="flex-1 min-h-0 relative">
        <CodeEditor
          value={draft}
          language={language}
          onChange={onChange}
          onSaveRequest={onSaveRequest}
          onViewReady={onViewReady}
          richMode={richMode}
          className="h-full"
        />
        <SaveBadge status={status} dirty={dirty} onSave={onSaveRequest} />
      </div>
    </div>
  );
}

/**
 * In-editor Save control. The header Save dot was removed (the header is
 * now just the preview/source toggle + ⋯); Save lives here next to the
 * code, alongside `Cmd-S` and the 900ms idle autosave. When there are
 * unsaved edits it's a clickable Save; otherwise it reflects status.
 */
function SaveBadge({
  status,
  dirty,
  onSave,
}: {
  status: 'idle' | 'saving' | 'saved' | 'error';
  dirty: boolean;
  onSave?: () => void;
}) {
  const t = useT();
  let label: string;
  let icon: React.ReactNode;
  let tone = 'text-muted-foreground';
  let clickable = false;
  if (status === 'error') {
    label = t('workspace.node.saveFailedRetry');
    icon = <AlertTriangle className="h-3 w-3" />;
    tone = 'text-amber-600 dark:text-amber-400';
    clickable = true;
  } else if (status === 'saving') {
    label = t('workspace.canvas.saving');
    icon = <Loader2 className="h-3 w-3 animate-spin" />;
  } else if (dirty) {
    label = t('actions.save');
    icon = <Save className="h-3 w-3" />;
    tone = 'text-foreground';
    clickable = true;
  } else if (status === 'saved') {
    label = t('discover.scopeSaved');
    icon = <Check className="h-3 w-3" />;
    tone = 'text-emerald-600 dark:text-emerald-400';
  } else {
    label = t('discover.scopeSaved');
    icon = <Check className="h-3 w-3" />;
  }
  const inner = (
    <span
      className={cn(
        'glass-surface inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium shadow-sm',
        tone,
      )}
    >
      {icon}
      {label}
    </span>
  );
  return (
    <div className="absolute top-2 right-2 z-10">
      {clickable && onSave ? (
        <button
          type="button"
          onClick={onSave}
          aria-label={t('actions.save')}
          className="nodrag nopan cursor-pointer outline-none transition-transform hover:scale-105 active:scale-95"
        >
          {inner}
        </button>
      ) : (
        <div role="status" aria-live="polite" className="pointer-events-none">
          {inner}
        </div>
      )}
    </div>
  );
}

function ContentPreviewBody({
  meta,
  htmlSource,
  onOpenLightbox,
  disableLightboxOnClick = false,
  onNaturalSize,
  onContentSize,
  onLog,
  onLoadState,
}: {
  meta: ContentMeta;
  /** Loaded HTML body for srcDoc preview (workspace COEP-safe path). */
  htmlSource?: string | null;
  onOpenLightbox: () => void;
  /** HTML widget reported its natural size → auto-fit the tile (once). */
  onContentSize?: (width: number, height: number) => void;
  /** HTML widget forwarded a console/error line → buffer it for agents. */
  onLog?: (entry: WidgetLogEntry) => void;
  /** HTML widget became ready, or failed to load (blank/CSP-blocked). */
  onLoadState?: (state: 'ready' | 'failed') => void;
  /**
   * When true, suppress the click-to-lightbox affordance on
   * image / video / model3d bodies. Used for chromeless content
   * nodes (`state.hideHeader: true`) where the body is promoted
   * to a drag-handle — see node-frame.tsx + content-node.tsx.
   * The lightbox is still reachable from the right-click "Open
   * in full editor" menu entry, so we don't lose the affordance,
   * just relocate it.
   */
  disableLightboxOnClick?: boolean;
  /**
   * Fires once the underlying media reports its intrinsic
   * dimensions:
   *
   *   - image: `<img onLoad>` → `naturalWidth` / `naturalHeight`.
   *   - video: `<video onLoadedMetadata>` → `videoWidth` /
   *     `videoHeight`.
   *
   * Caller (the parent `ContentNode`) uses the values to record
   * `state.cache.aspectRatio` and reflow the node bounds to match
   * the source aspect on first paint. Optional; if omitted the
   * media renders with its existing bounds untouched.
   */
  onNaturalSize?: (width: number, height: number) => void;
}) {
  const t = useT();
  const { contentType, mimeType, url, previewUrl, displayName } = meta;
  const src = url ?? `/api/content/${meta.id}`;

  if (meta.unavailable) {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center"
        data-testid="content-node-unavailable"
      >
        <Lock className="h-5 w-5 text-muted-foreground" aria-hidden />
        <div className="max-w-full truncate text-sm font-medium text-foreground">{displayName}</div>
        <div className="max-w-xs text-xs text-muted-foreground">{t('workspace.canvas.node.unavailable')}</div>
      </div>
    );
  }

  if (contentType === 'image') {
    // Real `<img>` element (not a CSS background) so:
    //   - Transparent PNGs / SVGs let the canvas grid show
    //     through. The previous `bg-muted/30` parent + `background-
    //     image` combo painted a grey tile behind every image,
    //     which looked correct for photos but broke artwork
    //     pastes with alpha channels.
    //   - SVGs render crisp at any zoom level — `<img>` rasterises
    //     them on-demand at the displayed size, unlike a CSS
    //     background which can interpolate. Scripts inside an
    //     SVG loaded via `<img>` never execute, so no DOMPurify
    //     layer is needed (browser-enforced for security).
    //   - `object-contain` preserves the natural aspect ratio
    //     inside the node bounds, mirroring the "fit, don't
    //     crop" expectation users have for pasted art. The
    //     previous `background-size: cover` cropped non-square
    //     images on a square 360x360 default.
    // SVG: always use the original vector. A `previewUrl` here would
    // be a legacy JPEG `_thumb.jpg` (alpha flattened onto black) left
    // over from before we stopped rasterising SVGs — using it would
    // re-introduce the black-background bug on the canvas.
    const isSvg = mimeType === 'image/svg+xml';
    const imgSrc = isSvg ? src : previewUrl || src;
    // Chromeless image (headerless content node): render a plain
    // <div> with no click handler so the body's drag-handle in
    // NodeFrame gets every pointerdown. A <button> would still
    // hijack focus + emit a synthetic click on pointerup, which
    // breaks the perception that this is "just a picture you can
    // drag". Hover dimmer is dropped too — pure picture, no
    // interactive affordance.
    const onImgLoad = onNaturalSize
      ? (e: React.SyntheticEvent<HTMLImageElement>) => {
          const t = e.currentTarget;
          if (t.naturalWidth > 0 && t.naturalHeight > 0) {
            onNaturalSize(t.naturalWidth, t.naturalHeight);
          }
        }
      : undefined;
    if (disableLightboxOnClick) {
      return (
        <div className="relative block h-full w-full">
          <img
            src={imgSrc}
            alt={displayName}
            draggable={false}
            onLoad={onImgLoad}
            className="h-full w-full select-none object-contain"
          />
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={onOpenLightbox}
        aria-label={displayName}
        className="group relative block h-full w-full"
      >
        <img
          src={imgSrc}
          alt={displayName}
          draggable={false}
          onLoad={onImgLoad}
          className="h-full w-full select-none object-contain"
        />
        <span className="pointer-events-none absolute inset-0 transition-colors group-hover:bg-black/10" />
      </button>
    );
  }
  if (contentType === 'video') {
    return (
      <div
        className={cn(
          'h-full w-full flex items-center justify-center',
          // Black letterboxing makes sense when the chrome card
          // wraps the video and aspect ratio can mismatch; the
          // headerless flow reflows the node bounds to match the
          // intrinsic aspect (see `onNaturalSize` plumbing above)
          // so no bars are needed and `bg-black` would just be a
          // dark frame around the picture.
          disableLightboxOnClick ? 'bg-transparent' : 'bg-black',
        )}
      >
        <VideoPreview
          src={src}
          className="h-full w-full"
          // Mirror the image branch: a headerless video node
          // hands every click to the drag-handle in NodeFrame.
          // Right-click → "Open in full editor" still surfaces
          // the lightbox.
          onClick={disableLightboxOnClick ? undefined : onOpenLightbox}
          onNaturalSize={onNaturalSize}
        />
      </div>
    );
  }
  if (contentType === 'audio') {
    return (
      <div className="h-full w-full p-3 flex items-center justify-center">
        <AudioPreview src={src} />
      </div>
    );
  }
  if (contentType === 'model3d') {
    return (
      <div className="h-full w-full">
        <Model3DPreview src={src} className="h-full" onClick={onOpenLightbox} />
      </div>
    );
  }
  if (isHtml(mimeType, displayName)) {
    return (
      <HtmlPreviewIframe
        html={htmlSource ?? null}
        title={displayName}
        // Pass `meta.id` so the iframe can use the sandbox-subdomain
        // route — runs the HTML in a separate origin from apex,
        // unblocks `localStorage`, keeps apex cookies safe. Falls
        // back to apex-origin srcdoc when missing.
        contentId={meta.id}
        onContentSize={onContentSize}
        onLog={onLog}
        onLoadState={onLoadState}
      />
    );
  }
  return (
    <div className="h-full w-full p-3">
      <FileCard
        url={src}
        filename={displayName}
        mimeType={mimeType}
        className="h-full"
      />
      <Link
        href={`/content/${meta.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <Maximize2 className="h-3 w-3" /> Open
      </Link>
    </div>
  );
}

/**
 * Centered, glass-panel settings dialog for a content / app node. Wears the
 * same chrome as the workspace-settings dialog (see {@link
 * WorkspaceSettingsDialog}) so per-app settings read as part of one family.
 * Portals to <body> via PopoverPortal, so it floats above the canvas
 * regardless of the node's size or stacking context and the app underneath
 * stays mounted. Esc, the X, and a backdrop click all close it.
 */
function WidgetSettingsModal({
  meta,
  onMetaChange,
  itemId,
  showPermissions = false,
  onClose,
}: {
  meta: ContentMeta;
  onMetaChange: (patch: Partial<ContentMeta>) => void;
  itemId: string;
  showPermissions?: boolean;
  onClose: () => void;
}) {
  const t = useT();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={t('workspace.node.appSettings')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 220,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '8vh 4vw',
      }}
      className="bg-black/40 backdrop-blur-sm"
    >
      {/* Backdrop click-away. Sits behind the card, which stops propagation. */}
      <div className="absolute inset-0" aria-hidden onMouseDown={onClose} />
      <div
        className="glass-panel relative z-[1] flex max-h-[84vh] w-[min(560px,96vw)] flex-col overflow-hidden rounded-2xl shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <h2 className="text-sm font-semibold tracking-tight">{t('workspace.node.appSettings')}</h2>
          <div className="ml-auto" />
          <button
            type="button"
            aria-label={t('workspace.canvas.node.close')}
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="nowheel ws-selectable min-h-0 flex-1 overflow-auto px-4 py-4">
          <ContentSettingsBody
            meta={meta}
            onMetaChange={onMetaChange}
            itemId={itemId}
            showPermissions={showPermissions}
          />
        </div>
      </div>
    </PopoverPortal>
  );
}

/**
 * One settings section in the workspace-settings visual family: an icon chip
 * (primary-tinted, like {@link WorkspaceSettingsDialog}'s rows) beside a
 * medium-weight title, a snug muted description, and the control below.
 * Purely presentational; keeps every section reading as one family.
 */
function SettingsSection({
  icon: Icon,
  title,
  htmlFor,
  description,
  children,
}: {
  icon: LucideIcon;
  title: string;
  htmlFor?: string;
  description?: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="flex items-start gap-3">
      <div className="mt-0.5 shrink-0 rounded-md bg-primary/10 p-1.5 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="block text-sm font-medium">
            {title}
          </label>
        ) : (
          <div className="text-sm font-medium">{title}</div>
        )}
        {description && (
          <p className="text-[12px] text-muted-foreground leading-snug">{description}</p>
        )}
        {children}
      </div>
    </section>
  );
}

/**
 * Segmented control shared by the Visibility + Agent-control rows — one
 * rounded track, the active option raised on a background chip. Matches the
 * segmented idiom used across the workspace chrome instead of a loose row of
 * outline buttons.
 */
function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled,
}: {
  options: ReadonlyArray<{ id: T; label: string; icon?: LucideIcon }>;
  value: T;
  onChange: (id: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex max-w-full flex-wrap gap-0.5 rounded-lg border border-border/70 bg-muted/40 p-0.5">
      {options.map((opt) => {
        const active = value === opt.id;
        const Icon = opt.icon;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            disabled={disabled}
            aria-pressed={active}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs transition-colors',
              active
                ? 'bg-background font-medium text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Settings form body for a content / app node. Rendered inside
 * {@link WidgetSettingsModal}; persists changes optimistically: each control
 * PATCHes the relevant endpoint and bubbles the new value back up to the
 * node's `meta` so the next paint reflects it without waiting on a refetch.
 */
function ContentSettingsBody({
  meta,
  onMetaChange,
  itemId,
  showPermissions = false,
}: {
  meta: ContentMeta;
  onMetaChange: (patch: Partial<ContentMeta>) => void;
  itemId: string;
  /** Only iframe-kind widgets surface the per-widget permission grants. */
  showPermissions?: boolean;
}) {
  const t = useT();
  const [draftName, setDraftName] = useState(meta.displayName);
  const [savingName, setSavingName] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setDraftName(meta.displayName);
  }, [meta.displayName]);
  // The row's live name in this tab (item-label.ts). `saveName` treats a name
  // as unchanged only when it matches this AND `meta`, since either one can be
  // the stale side for a moment; a redundant PATCH is harmless, a dropped
  // rename is not.
  const liveName = useWorkspaceContent(meta.id)?.displayName;

  const isPublic = meta.linkVisibility === 'public';
  const isUnlisted = meta.linkVisibility === 'unlisted';

  // ---- Per-widget feature permissions (B4 / Part D) ----
  // The user is the grant authority; this panel is the manage/revoke
  // surface. Toggling persists `state.permissions` via the store (which
  // re-keys the iframe → reload with the new Permissions-Policy). For
  // device APIs we also reconcile the portal-origin browser permission.
  const permItem = useWorkspaceItem(itemId);
  const { patchItemState: patchPerms } = useWorkspaceActions();
  const wsForGrants = useWorkspace();
  const granted = useMemo(
    () => readGrantedPermissions(permItem?.state),
    [permItem?.state],
  );
  const grantedSet = useMemo(() => new Set(granted), [granted]);
  // `files` may be granted SCOPED (`files:<folderId>` from the host access
  // dialog); the chip reflects either form, and revoking clears both.
  const featureOn = useCallback(
    (feature: string) =>
      grantedSet.has(feature) ||
      (feature === 'files' && granted.some((g) => g.startsWith('files:'))),
    [granted, grantedSet],
  );
  const toggleFeature = useCallback(
    (feature: string) => {
      // Sensitive features live in the PER-USER local grant store (never the
      // shared doc — an editor must not arm a widget in other users'
      // browsers; see widget-permissions-local.ts). The store re-projects on
      // change, which re-keys the iframe.
      if (isSensitiveFeature(feature)) {
        const wsId = wsForGrants?.id;
        if (!wsId) return;
        if (featureOn(feature)) {
          removeLocalWidgetGrant(wsId, itemId, feature);
          if (feature === 'files') {
            for (const g of granted) {
              if (g.startsWith('files:')) removeLocalWidgetGrant(wsId, itemId, g);
            }
          }
        } else {
          addLocalWidgetGrant(wsId, itemId, feature);
          // Device features (camera/mic/geo) also need the portal-origin
          // browser site-permission — fire the native prompt now. `files`
          // is bridge-gated, not a browser capability.
          if (feature !== 'files') {
            void import('@/lib/workspaces/permission-reconcile')
              .then(({ reconcilePortalCapability }) =>
                reconcilePortalCapability(feature),
              )
              .catch(() => {});
          }
        }
        return;
      }
      // Non-sensitive tokens keep the legacy shared-state path.
      if (grantedSet.has(feature)) {
        patchPerms(itemId, { permissions: granted.filter((f) => f !== feature) });
      } else {
        patchPerms(itemId, { permissions: [...granted, feature] });
      }
    },
    [granted, grantedSet, featureOn, itemId, patchPerms, wsForGrants?.id],
  );

  // ---- Per-widget AGENT control (who may drive this app via commands) ----
  // Persists `state.agentControl` (TrustRules) through the store so the
  // command bus sees the grant live (no reload). The allow-list keys are
  // agent DB ids (the agent item's `refId`), which is exactly what the bus
  // precheck matches — granting by @handle would silently no-op.
  const allItems = useWorkspaceItems();
  const agentRules = permItem?.state?.agentControl;
  const agentAllow = useMemo(
    () => (Array.isArray(agentRules) ? agentRules : agentRules?.allow ?? []),
    [agentRules],
  );
  const agentAllowSet = useMemo(() => new Set(agentAllow), [agentAllow]);
  // The chip list is the UNION of agents on this canvas and ids already on the
  // allow-list. Listing only canvas agents would hide (and make unrevokable) an
  // agent granted through Drive or the consent prompt and then removed from the
  // canvas — the grant outlives the item, so the revoke affordance must too.
  const grantableAgents = useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    for (const it of allItems) {
      if (it.type !== 'agent' || !it.refId) continue;
      byId.set(it.refId, {
        id: it.refId,
        name:
          it.state?.cache?.displayName || it.state?.cache?.username || 'Agent',
      });
    }
    for (const id of agentAllow) {
      if (id === 'everyone' || id === '*' || byId.has(id)) continue;
      // Granted but no longer on the canvas: show the id so it can be revoked.
      byId.set(id, { id, name: `Agent ${id.slice(0, 8)}` });
    }
    return [...byId.values()];
  }, [allItems, agentAllow]);
  // Three states, and the allow-list is the whole truth: 'off' is an empty
  // list, 'all' is the `everyone` wildcard, 'specific' is named agent ids.
  // There is no fourth "inherit the workspace" mode — that setting is gone.
  const computeAgentMode = useCallback((): 'off' | 'all' | 'specific' => {
    if (agentAllow.includes('everyone') || agentAllow.includes('*')) return 'all';
    if (agentAllow.length > 0) return 'specific';
    return 'off';
  }, [agentAllow]);
  const [agentMode, setAgentMode] = useState<'off' | 'all' | 'specific'>(
    computeAgentMode,
  );
  // Re-sync the segmented control when rules change elsewhere (other tab /
  // collaborator). A user mid-edit in 'specific' with an empty list stays put.
  useEffect(() => {
    setAgentMode((m) => {
      const derived = computeAgentMode();
      if (m === 'specific' && derived === 'off') return m;
      return derived;
    });
  }, [computeAgentMode]);
  const applyAgentMode = useCallback(
    (mode: 'off' | 'all' | 'specific') => {
      setAgentMode(mode);
      if (mode === 'off') {
        patchPerms(itemId, { agentControl: { allow: [] } });
        /* OFF WITHDRAWS THE PER-APP YES TOO (the review of 2026-10-08). The
           canvas approval of the person's own app agent is recorded as the
           per-app consent ADR-0048 keeps for `/w/` (lib/workspaces/
           own-app-agent-grants.ts), and nothing in the UI withdrew it. Off
           on the app's window is that withdrawal: the same route the `/w/`
           dialog writes, `allow: false`. The route answers 404 for an app
           with no inbox or no agent of the person's, which is nothing to
           withdraw; best-effort, the window's own list is already empty. */
        const widgetType = typeof meta.metadata?.widgetType === 'string' ? meta.metadata.widgetType : null;
        if (widgetType && meta.id) {
          void fetch('/api/widgets/agent/operate', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ widgetType, contentId: meta.id, allow: false }),
          }).catch(() => {
            /* nothing to withdraw, or no network: the window is off either way */
          });
        }
      } else if (mode === 'all')
        patchPerms(itemId, { agentControl: { allow: ['everyone'] } });
      else
        patchPerms(itemId, {
          agentControl: {
            allow: agentAllow.filter((a) => a !== 'everyone' && a !== '*'),
          },
        });
    },
    [agentAllow, itemId, patchPerms, meta.id, meta.metadata?.widgetType],
  );
  const toggleAgent = useCallback(
    (agentId: string) => {
      const base = agentAllow.filter((a) => a !== 'everyone' && a !== '*');
      const next = base.includes(agentId)
        ? base.filter((a) => a !== agentId)
        : [...base, agentId];
      patchPerms(itemId, { agentControl: { allow: next } });
    },
    [agentAllow, itemId, patchPerms],
  );

  const saveName = async () => {
    const trimmed = draftName.trim();
    if (!trimmed) return;
    if (trimmed === meta.displayName && (liveName === undefined || trimmed === liveName)) return;
    setSavingName(true);
    try {
      const res = await fetch(`/api/content/${meta.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: trimmed }),
      });
      if (res.ok) {
        onMetaChange({ displayName: trimmed });
        // The tile's own meta is local state; every other label in this tab
        // (rail, Stage, overview, menus) reads the live content cache, which
        // only this announcement refreshes (item-label.ts, 2026-09-13).
        announceContentRenamed(meta.id, trimmed);
      }
    } finally {
      setSavingName(false);
    }
  };

  const setVisibility = async (vis: 'private' | 'public' | 'unlisted') => {
    setSavingVisibility(true);
    try {
      const res = await fetch(`/api/content/${meta.id}/link`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibility: vis }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => null);
        onMetaChange({
          linkVisibility: vis,
          shareToken: data?.shareToken ?? meta.shareToken,
        });
      }
    } finally {
      setSavingVisibility(false);
    }
  };

  const shareUrl =
    typeof window !== 'undefined' && meta.url
      ? `${window.location.origin}${meta.url}`
      : '';
  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const inputCls =
    'w-full h-8 px-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 transition';

  return (
    <div className="text-sm">
      <div className="space-y-5">
        <SettingsSection icon={PenLine} title={t('workspace.node.name')} htmlFor={`name-${meta.id}`}>
          <div className="flex gap-1.5">
            <input
              id={`name-${meta.id}`}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
              }}
              className={inputCls}
            />
            {draftName.trim() !== meta.displayName && (
              <button
                type="button"
                onClick={saveName}
                disabled={savingName}
                className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'h-8 shrink-0')}
              >
                {t('actions.save')}
              </button>
            )}
          </div>
        </SettingsSection>

        <SettingsSection
          icon={Eye}
          title={t('workspace.node.visibility')}
          description={
            isPublic || isUnlisted
              ? 'Anyone holding the link can open it.'
              : 'Anyone who opens your link is added as a viewer.'
          }
        >
          {/* Same two rungs as the Share dialog, and for the same reason:
              'unlisted' and 'public' grant identical access, and no surface
              lists documents, so a third option changed nothing observable
              while reading as more access. Existing 'public' rows keep
              working and simply show as open; choosing it here writes
              'unlisted'. Wording matches the dialog so the two controls
              cannot read as different settings. */}
          <SegmentedControl
            options={[
              { id: 'private', label: 'Only people I invite', icon: Lock },
              { id: 'unlisted', label: 'Anyone with the link', icon: Link2 },
            ]}
            value={(meta.linkVisibility ?? 'private') === 'private' ? 'private' : 'unlisted'}
            onChange={setVisibility}
            disabled={savingVisibility}
          />
        </SettingsSection>

        {shareUrl && (isPublic || isUnlisted) && (
          <SettingsSection icon={Link2} title={t('workspace.node.shareLink')}>
            <div className="flex gap-1.5">
              <input
                value={shareUrl}
                readOnly
                className={`${inputCls} font-mono text-xs`}
              />
              <button
                type="button"
                onClick={copyLink}
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'h-8 shrink-0')}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          </SettingsSection>
        )}

        {showPermissions && (
          <SettingsSection
            icon={Shield}
            title={t('workspace.node.permissions')}
            description={t('workspace.node.browserFeaturesNote')}
          >
            <div className="flex flex-wrap gap-1.5">
              {SENSITIVE_FEATURES.map((feature) => {
                const on = featureOn(feature);
                return (
                  <button
                    key={feature}
                    type="button"
                    onClick={() => toggleFeature(feature)}
                    aria-pressed={on}
                    className={cn(
                      'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] transition-colors',
                      on
                        ? 'border-primary/40 bg-primary/10 font-medium text-primary'
                        : 'border-border/70 text-muted-foreground hover:border-border hover:text-foreground',
                    )}
                    title={
                      on
                        ? t('workspace.node.featureRevoke', { feature })
                        : t('workspace.node.featureGrant', { feature })
                    }
                  >
                    {on ? (
                      <ShieldCheck className="h-3 w-3" />
                    ) : (
                      <Shield className="h-3 w-3 opacity-60" />
                    )}
                    <span className="font-mono">{feature}</span>
                  </button>
                );
              })}
            </div>
          </SettingsSection>
        )}

        <SettingsSection
          icon={Bot}
          title={t('workspace.node.agentControl')}
          description={t('workspace.node.agentControlDesc')}
        >
          <SegmentedControl
            options={[
              { id: 'off', label: t('workspace.node.agentModeOff') },
              { id: 'specific', label: t('workspace.node.agentModeChosen') },
              { id: 'all', label: t('workspace.node.agentModeAny') },
            ]}
            value={agentMode}
            onChange={applyAgentMode}
          />
          {agentMode === 'off' && (
            <p className="text-[12px] text-muted-foreground leading-snug">
              {t('workspace.node.agentControlNone')}
            </p>
          )}
          {agentMode === 'all' && (
            <p className="text-[12px] text-muted-foreground leading-snug">
              {t('workspace.node.agentControlAny')}
            </p>
          )}
          {agentMode === 'specific' && (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {grantableAgents.length === 0 ? (
                <span className="text-[12px] text-muted-foreground leading-snug">
                  {t('workspace.node.noAgentsOnCanvas')}
                </span>
              ) : (
                grantableAgents.map((ag) => {
                  const on = agentAllowSet.has(ag.id);
                  return (
                    <button
                      key={ag.id}
                      type="button"
                      onClick={() => toggleAgent(ag.id)}
                      aria-pressed={on}
                      className={cn(
                        'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] transition-colors',
                        on
                          ? 'border-primary/40 bg-primary/10 font-medium text-primary'
                          : 'border-border/70 text-muted-foreground hover:border-border hover:text-foreground',
                      )}
                      title={
                        on
                          ? t('workspace.node.agentRevoke', { name: ag.name })
                          : t('workspace.node.agentAllow', { name: ag.name })
                      }
                    >
                      {on ? (
                        <ShieldCheck className="h-3 w-3" />
                      ) : (
                        <Shield className="h-3 w-3 opacity-60" />
                      )}
                      <span className="truncate max-w-[120px]">{ag.name}</span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </SettingsSection>

        {/* PEOPLE SEE "App", NEVER THE INTERNAL KIND OR ITS MIME TYPE (the e2e
            visual pass of 2026-10-06, RV-B1: App settings read "Kind Widget"
            beside the widget MIME type, under English labels). An app's row
            names it an App and drops the MIME type, which is plumbing; any
            other content keeps its file type, and every label is translated. */}
        <SettingsSection icon={Info} title={t('workspace.node.metadata')}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-[12px]">
            {meta.contentType !== 'widget' && meta.mimeType && (
              <>
                <dt className="text-muted-foreground">{t('workspace.node.metaType')}</dt>
                <dd className="font-mono truncate">{meta.mimeType}</dd>
              </>
            )}
            <dt className="text-muted-foreground">{t('workspace.node.metaKind')}</dt>
            <dd className="capitalize">{meta.contentType === 'widget' ? t('workspace.node.kindApp') : meta.contentType}</dd>
            {meta.byteSize != null && (
              <>
                <dt className="text-muted-foreground">{t('workspace.node.metaSize')}</dt>
                <dd>{formatBytes(meta.byteSize)}</dd>
              </>
            )}
            {meta.updatedAt && (
              <>
                <dt className="text-muted-foreground">{t('workspace.node.metaUpdated')}</dt>
                <dd>{formatRelativeTime(meta.updatedAt)}</dd>
              </>
            )}
            <dt className="text-muted-foreground">{t('workspace.node.metaId')}</dt>
            <dd className="font-mono text-[11px] truncate">{meta.id}</dd>
          </dl>
        </SettingsSection>

      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
